import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import './Game_Home.css';
import GameAPI from './GameAPI';
import GameTileExperience from '../GameWorld/GameTileExperience';
import ToastHost from '../common/ToastHost';
import ToastService from '../../services/ToastService';
import WebSocketService from '../../services/WebSocketService';
import CombatOverlay, { DEFAULT_ENEMY_TYPE_COLOR } from './Home/CombatOverlay';
import CombatRewardsPopup from './Home/CombatRewardsPopup';
import IdleOverlay from './Home/IdleOverlay';
import PlayerPanel from './Home/PlayerPanel';
import RegionPanel from './Home/RegionPanel';
import EquipmentPanel from './Home/EquipmentPanel';
import InventoryPanel from './Home/InventoryPanel';
import ActionsPanel from './Home/ActionsPanel';
import EventLog from './Home/EventLog';
import GameMenu from './Home/GameMenu';
import StatusBar, { HungerStatusEffect } from './Home/StatusBar';
import ConsumableEffectsHud from './Home/ConsumableEffectsHud';
import QuickBelt from './QuickBelt';
import {
  describeConsumableUseEffects,
  getConsumableEffects,
  isTimedConsumableEffect,
} from './consumableEffects';
import { GatheringStatus } from './Game_DesiredActions';
import useWindowManager from './Home/useWindowManager';
import useEventLog from './Home/useEventLog';
import useGameConnection from './Home/useGameConnection';
import Button from '../../ui/Button';
import FloatingWindow from '../../ui/FloatingWindow';
import IconButton from '../../ui/IconButton';
import ProgressBar from '../../ui/ProgressBar';
import { apiErrorCode } from '../../Api';
import AvatarCreatorWindow from '../Avatar/AvatarCreatorWindow';
import GroundLootDropDialog from './GroundLoot/GroundLootDropDialog';
import GroundLootTransferDialog from './GroundLoot/GroundLootTransferDialog';
import BaseCampScene from './BaseCamp/BaseCampScene';
import WorldTravelDialog from './BaseCamp/WorldTravelDialog';
import useBaseCampActions from './BaseCamp/useBaseCampActions';
import useGatheringActions from './Gathering/useGatheringActions';
import {
  isCollectingAction,
} from './Gathering/gathering';
import {
  canUseGroundLoot,
  containsGroundLootEntry,
  createGroundLootOperationId,
  groundLootSummarySignature,
  groundLootErrorMessage,
  isDropSourceUnchanged,
  isNearGroundLoot,
  isTerminalGroundLootDropError,
  isTerminalGroundLootTakeError,
  normalizeGroundLootDetail,
  normalizeStarterCacheDetail,
  normalizeWorldLootDetail,
} from './GroundLoot/groundLoot';
import { getAvatarConfig, getPlayerAvatar, savePlayerAvatar } from '../Avatar/AvatarAPI';
import {
  buildCombatTimer,
  buildCombatTimerFromEvent,
  buildFleeTimerFromState,
  localizeServerTimestamp,
} from './Home/combatTimers';
import { enqueueCombatEffect } from './Home/combatEvents';
import '../../ui/design-system.css';

export const PANEL_CONFIG = {
  playerInfo: {
    title: 'Operative Status',
    icon: 'operative',
    anchor: 'top-left',
    defaultOpen: true,
    shortcut: 'P',
  },
  regionInfo: {
    title: 'Region Intel',
    icon: 'region',
    anchor: 'right-center',
    defaultOpen: false,
    shortcut: 'L',
  },
  equipment: {
    title: 'Loadout',
    icon: 'gear',
    anchor: 'bottom-left',
    defaultOpen: false,
    shortcut: 'E',
  },
  inventory: {
    title: 'Pack',
    icon: 'pack',
    anchor: 'bottom-right',
    defaultOpen: false,
    defaultSize: { width: 344, height: 516 },
    minSize: { width: 260, height: 390 },
    maxSize: { width: 344, height: 516 },
    scaleSizeRange: true,
    resizable: true,
    shortcut: 'I',
  },
  eventLog: {
    title: 'Event Log',
    icon: 'log',
    anchor: 'top-right',
    defaultOpen: true,
    defaultSize: { width: 420, height: 360 },
    minSize: { width: 300, height: 220 },
    maxSize: { width: 720, height: 720 },
    resizable: true,
    shortcut: 'O',
  },
};

const PANEL_ORDER = Object.keys(PANEL_CONFIG);
const LAUNCHER_PANEL_ORDER = PANEL_ORDER.filter(key => !['playerInfo', 'regionInfo'].includes(key));
export const MOBILE_TOOL_PANEL_ORDER = [
  'equipment',
  'eventLog',
];

const PLAYER_XP_CURVE_FACTOR = 25;

export function resolveWindowRenderOrder(windowState) {
  return [
    ...PANEL_ORDER.filter(key => windowState[key]?.open && !windowState[key]?.pinned),
    ...PANEL_ORDER.filter(key => windowState[key]?.open && windowState[key]?.pinned),
    ...PANEL_ORDER.filter(key => !windowState[key]?.open),
  ];
}

export function resolvePlayerXpProgress(rawXp) {
  const parsedXp = Number(rawXp);
  const xp = Number.isFinite(parsedXp) && parsedXp > 0 ? parsedXp : 0;
  const level = Math.max(1, Math.floor(
    (1 + Math.sqrt(1 + ((4 * xp) / PLAYER_XP_CURVE_FACTOR))) / 2,
  ));
  const currentLevelXp = PLAYER_XP_CURVE_FACTOR * level * (level - 1);
  const xpForNextLevel = PLAYER_XP_CURVE_FACTOR * level * (level + 1);
  const levelSpan = xpForNextLevel - currentLevelXp;
  const xpProgress = levelSpan > 0
    ? ((xp - currentLevelXp) / levelSpan) * 100
    : 0;

  return {
    currentLevelXp,
    level,
    xpForNextLevel,
    xpProgress: Math.min(100, Math.max(0, xpProgress)),
  };
}

export function resolveDisplayedEnemyLevel(userCombat, regionEnemyLevel) {
  const combatLevel = Number(userCombat?.enemyLevel);
  if (Number.isInteger(combatLevel) && combatLevel > 0) {
    return combatLevel;
  }

  const fallbackLevel = Number(regionEnemyLevel);
  return Number.isInteger(fallbackLevel) && fallbackLevel > 0 ? fallbackLevel : undefined;
}

export function isCombatDisplayStat(key, label) {
  const normalized = `${key || ''}${label || ''}`.toLowerCase().replace(/[^a-z0-9]/g, '');
  return !normalized.includes('magicfind');
}

export function areInventoryActionsDisabled(player) {
  const action = String(player?.currentAction || '').toUpperCase();
  return Boolean(player?.userCombat)
    || ['FIGHTING', 'FLEEING', 'DEAD'].includes(action);
}

export function resolveCombatEnemy(player) {
  if (!['FIGHTING', 'FLEEING'].includes(player?.currentAction)) return null;
  return player?.userCombat?.enemy ?? null;
}

function combatRewardManifestEntries(entries) {
  return (Array.isArray(entries) ? entries : []).map((entry, index) => {
    const itemId = entry?.itemId ?? entry?.ItemId ?? '';
    const itemInstanceId = entry?.itemInstanceId
      ?? entry?.ItemInstanceId
      ?? '';
    const entryId = entry?.entryId ?? entry?.EntryId ?? '';
    const name = entry?.name ?? entry?.Name ?? 'Unknown item';
    const imageId = entry?.imageId ?? entry?.ImageId ?? '';
    return {
      key: String(
        entryId
        || itemInstanceId
        || `${itemId || 'reward'}:${index}`,
      ),
      entryId: String(entryId),
      itemId: String(itemId),
      itemInstanceId: String(itemInstanceId),
      item: entry?.item ?? entry?.Item ?? { name, imageId },
      name: String(name),
      quantity: Math.max(
        1,
        Number(entry?.quantity ?? entry?.Quantity) || 1,
      ),
    };
  });
}

export function reduceCombatReward(summary, event) {
  if (event.kind === 'start') {
    const manifestFinal = event.manifestFinal === true;
    return {
      rewardId: event.rewardId,
      enemyName: event.enemyName,
      xp: manifestFinal ? Math.max(0, Number(event.xp) || 0) : 0,
      loot: manifestFinal
        ? combatRewardManifestEntries(event.packLoot)
        : [],
      groundLoot: manifestFinal
        ? combatRewardManifestEntries(event.groundLoot)
        : [],
      groundContainer: manifestFinal
        ? event.groundContainer || null
        : null,
      manifestFinal,
      complete: manifestFinal,
    };
  }
  if (!summary || summary.rewardId !== event.rewardId) return summary;

  if (event.kind === 'xp') {
    if (summary.manifestFinal) return summary;
    return { ...summary, xp: summary.xp + event.amount };
  }
  if (event.kind === 'complete') {
    return { ...summary, complete: true };
  }
  if (event.kind !== 'loot' || summary.manifestFinal) return summary;

  const key = event.itemInstanceId || event.itemId;
  const existing = summary.loot.find(entry => entry.key === key);
  const loot = existing
    ? summary.loot.map(entry => entry.key === key
      ? { ...entry, quantity: entry.quantity + event.quantity }
      : entry)
    : [...summary.loot, {
      key,
      itemId: event.itemId,
      itemInstanceId: event.itemInstanceId,
      item: event.item,
      name: event.item?.name || 'Unknown item',
      quantity: event.quantity,
    }];
  return { ...summary, loot };
}

export function canAutoOpenCombatGroundLoot(summary, action, position) {
  return Boolean(
    summary?.complete
    && summary?.manifestFinal
    && summary?.groundContainer
    && (summary?.groundLoot || []).length > 0
    && canUseGroundLoot(action)
    && isNearGroundLoot(position, summary.groundContainer),
  );
}

export function resolveCombatGroundContainer(summary, groundLoot) {
  const containerId = summary?.groundContainer?.containerId;
  if (!containerId) return null;
  return (groundLoot || []).find(
    container => container.containerId === containerId,
  ) || null;
}

export function shouldShowStarterOnboarding(player) {
  return Boolean(player?.currentRunId)
    && player?.starterLoadoutReady === false;
}

export function starterCacheHasRequiredEntries(starterCache) {
  return Number(starterCache?.requiredEntryCount) > 0;
}

export function shouldDiscardLearnRecipeOperationId(error) {
  const status = Number(error?.status);
  const code = apiErrorCode(error);
  if (['OPERATION_IN_PROGRESS', 'OPERATION_PENDING'].includes(code)) {
    return false;
  }
  return Number.isInteger(status)
    && status >= 400
    && status < 500
    && ![408, 425, 429].includes(status);
}

export function regionTransitionUserSnapshot(result) {
  const currentRegionId = result?.targetRegionId
    ?? result?.TargetRegionId;
  if (!currentRegionId) return null;

  const currentWorldInstanceId = result?.worldInstanceId
    ?? result?.WorldInstanceId;
  const x = Number(result?.x ?? result?.X);
  const y = Number(result?.y ?? result?.Y);
  return {
    currentRegionId,
    ...(currentWorldInstanceId
      ? { currentWorldInstanceId }
      : {}),
    ...(Number.isInteger(x) ? { lastLocationX: x } : {}),
    ...(Number.isInteger(y) ? { lastLocationY: y } : {}),
    currentAction: 'RESTING',
    desiredAction: 'NONE',
    previousAction: null,
    activeResourceCollection: null,
    movementTargetX: null,
    movementTargetY: null,
    movementStopDistance: null,
    movementArrivalAction: null,
    movementPath: [],
    lastMoveDeltaX: 0,
    lastMoveDeltaY: 0,
  };
}

export async function reconcileRegionTransitionResult(result, {
  reconcileUserSnapshot,
  refreshBaseCamp,
  refreshUser,
}) {
  const transitionSnapshot = regionTransitionUserSnapshot(result);
  const responseUser = result?.user ?? result?.User;
  const userSnapshot = transitionSnapshot
    ? { ...(responseUser || {}), ...transitionSnapshot }
    : responseUser ?? (
      result?.currentRegionId !== undefined
      || result?.CurrentRegionId !== undefined
        ? result
        : null
    );
  const reconciled = userSnapshot
    ? reconcileUserSnapshot?.({
      ...result,
      user: userSnapshot,
    })
    : null;
  await Promise.allSettled([
    refreshUser?.(),
    refreshBaseCamp?.(),
  ]);
  return reconciled;
}

export function GameHome() {
  const [draggedItem, setDraggedItem] = useState(null);
  const [reduceMotion, setReduceMotion] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [idleOverlayTick, setIdleOverlayTick] = useState(() => Date.now());
  const [resumingIdle, setResumingIdle] = useState(false);
  const [idleResumeError, setIdleResumeError] = useState(null);
  const [combatTimers, setCombatTimers] = useState({ player: null, enemy: null });
  const [combatEffects, setCombatEffects] = useState([]);
  const [fleeProgress, setFleeProgress] = useState({
    attempt: 1,
    status: '',
    timer: null,
  });
  const [mobileActionsOpen, setMobileActionsOpen] = useState(false);
  const [enemyTypeColor, setEnemyTypeColor] = useState(DEFAULT_ENEMY_TYPE_COLOR);
  const [avatarConfig, setAvatarConfig] = useState(null);
  const [avatarSelection, setAvatarSelection] = useState(null);
  const [appearanceOpen, setAppearanceOpen] = useState(false);
  const [groundLootDropRequest, setGroundLootDropRequest] = useState(null);
  const [groundLootWindow, setGroundLootWindow] = useState(null);
  const [groundLootTransferBusy, setGroundLootTransferBusy] = useState(false);
  const [worldTravelRequest, setWorldTravelRequest] = useState(null);
  const [queuedWorldIntent, setQueuedWorldIntent] = useState(null);
  const [pendingCampEntry, setPendingCampEntry] = useState(null);
  const [combatReward, setCombatReward] = useState(null);
  const [combatRewardPinned, setCombatRewardPinned] = useState(false);
  const [scavengePreviewVisible, setScavengePreviewVisible] = useState(false);
  const [itemInteractionAnnouncement, setItemInteractionAnnouncement] = useState('');
  const menuRef = useRef(null);
  const combatEffectSequenceRef = useRef(0);
  const combatRewardDraftRef = useRef(null);
  const combatRewardAutoOpenIdsRef = useRef(new Set());
  const combatRewardRefreshKeysRef = useRef(new Set());
  const pendingCombatManifestRef = useRef(null);
  const openGroundLootContainerRef = useRef(null);
  const openGroundLootModeRef = useRef(null);
  const groundLootLoadSequenceRef = useRef(0);
  const groundLootWorldSignatureRef = useRef(null);
  const takeOperationIdsRef = useRef(new Map());
  const consumeOperationIdsRef = useRef(new Map());
  const learnRecipeOperationIdsRef = useRef(new Map());
  const salvageOperationIdsRef = useRef(new Map());
  const beltOperationIdsRef = useRef(new Map());
  const championEncounterOperationIdsRef = useRef(new Map());
  const queuedWorldIntentRef = useRef(null);
  const windowOpenersRef = useRef(new Map());
  const pendingWindowFocusRef = useRef('');

  const {
    focusWindow,
    moveWindow,
    resizeWindow,
    toggleCompact,
    togglePin,
    toggleWindow,
    windowLayers,
    windowState,
  } = useWindowManager(PANEL_CONFIG, PANEL_ORDER);

  const openWindow = useCallback((key, opener) => {
    if (windowState[key]?.open) {
      focusWindow(key);
      return;
    }
    if (opener?.focus) windowOpenersRef.current.set(key, opener);
    toggleWindow(key);
  }, [focusWindow, toggleWindow, windowState]);

  const closeWindow = useCallback((key) => {
    if (!windowState[key]?.open) return;
    pendingWindowFocusRef.current = key;
    toggleWindow(key);
  }, [toggleWindow, windowState]);

  const togglePanel = useCallback((key, opener) => {
    if (windowState[key]?.open) closeWindow(key);
    else openWindow(key, opener);
  }, [closeWindow, openWindow, windowState]);

  useEffect(() => {
    const key = pendingWindowFocusRef.current;
    if (!key || windowState[key]?.open) return;
    pendingWindowFocusRef.current = '';
    const opener = windowOpenersRef.current.get(key);
    if (opener?.isConnected) opener.focus();
  }, [windowState]);

  const openPack = useCallback((event) => {
    openWindow('inventory', event?.currentTarget);
  }, [openWindow]);

  const handleCombatEvent = useCallback((event) => {
    if (event.kind === 'snapshot') {
      if (event.action === 'FLEEING') {
        const fleeState = event.userCombat?.FleeState
          ?? event.userCombat?.fleeState;
        setCombatTimers({ player: null, enemy: null });
        setCombatEffects([]);
        setFleeProgress({
          attempt: Math.max(
            1,
            Number(fleeState?.TickCount ?? fleeState?.tickCount ?? 0) + 1,
          ),
          status: 'Finding an opening…',
          timer: buildFleeTimerFromState(fleeState, {
            sentAtMs: event.sentAtMs,
            receivedAtMs: event.receivedAtMs,
          }),
        });
        return;
      }

      const snapshotAt = localizeServerTimestamp(
        event.occurredAt,
        event.sentAtMs,
        event.receivedAtMs,
      ) ?? event.receivedAtMs;
      setCombatTimers({
        player: buildCombatTimer(event.userCombat?.PlayerRuntime ?? event.userCombat?.playerRuntime, snapshotAt),
        enemy: buildCombatTimer(event.userCombat?.EnemyRuntime ?? event.userCombat?.enemyRuntime, snapshotAt),
      });
      setCombatEffects([]);
      setFleeProgress({ attempt: 1, status: '', timer: null });
      return;
    }

    if (event.kind === 'flee') {
      const attempt = Math.max(1, Number(event.attempt) || 1);
      setFleeProgress({
        attempt: attempt + 1,
        status: event.result === 'success'
          ? `Flee attempt ${attempt} succeeded.`
          : `Flee attempt ${attempt} failed — trying again…`,
        timer: event.result === 'success'
          ? null
          : buildCombatTimerFromEvent({
            attackIntervalMs: event.timing?.attemptIntervalMs,
            nextAttackAtMs: event.timing?.nextAttemptAtMs,
          }, {
            sentAtMs: event.sentAtMs,
            receivedAtMs: event.receivedAtMs,
          }),
      });
      return;
    }

    if (event.timing) {
      setCombatTimers(prev => ({
        ...prev,
        [event.attacker]: buildCombatTimerFromEvent(
          event.timing,
          { sentAtMs: event.sentAtMs, receivedAtMs: event.receivedAtMs },
          prev[event.attacker],
        ),
      }));
    }

    combatEffectSequenceRef.current += 1;
    const sequence = combatEffectSequenceRef.current;
    setCombatEffects(prev => enqueueCombatEffect(prev, event, sequence, event.receivedAtMs));
  }, []);

  const handleCombatRewardEvent = useCallback((event) => {
    const next = reduceCombatReward(combatRewardDraftRef.current, event);
    combatRewardDraftRef.current = next;
    if (next?.complete && next?.manifestFinal) {
      pendingCombatManifestRef.current = next;
    }
    if (event.kind === 'start') {
      setCombatRewardPinned(false);
      setCombatReward(next?.complete ? { ...next } : null);
    } else if (next?.complete) {
      setCombatReward({ ...next });
    }
  }, []);

  const closeCombatReward = useCallback(() => {
    combatRewardDraftRef.current = null;
    setCombatReward(null);
    setCombatRewardPinned(false);
  }, []);

  const pinCombatReward = useCallback(() => {
    if (combatRewardDraftRef.current?.complete) {
      setCombatRewardPinned(true);
    }
  }, []);

  const {
    user,
    region,
    regionWorldState,
    starterCache,
    baseCamp,
    baseCampLoading,
    baseCampError,
    resourceNodesLoading,
    resourceNodesError,
    messages,
    connectionStatus,
    connectionError,
    retryConnection,
    reconcileInventory,
    reconcileBaseCampSnapshot,
    reconcileUserSnapshot,
    refreshBaseCamp,
    refreshGroundLoot,
    refreshResourceNodes,
    refreshStarterCache,
    refreshUser,
  } = useGameConnection(handleCombatEvent, handleCombatRewardEvent);

  const userCombat = user?.userCombat;
  const hasUserCombat = Boolean(userCombat);
  const playerRuntime = userCombat?.playerRuntime;
  const enemyRuntime = userCombat?.enemyRuntime;
  const enemyTypeId = userCombat?.enemy?.enemyType;
  const combatEnemyId = userCombat?.enemy?.id;
  const playerAttackInterval = playerRuntime?.attackInterval;
  const playerNextAttackTimer = playerRuntime?.nextAttackTimer;
  const enemyAttackInterval = enemyRuntime?.attackInterval;
  const enemyNextAttackTimer = enemyRuntime?.nextAttackTimer;
  const fleeState = userCombat?.fleeState;

  useEffect(() => {
    let active = true;
    setEnemyTypeColor(DEFAULT_ENEMY_TYPE_COLOR);
    if (enemyTypeId) {
      GameAPI.fetchEnemyType(enemyTypeId).then((enemyType) => {
        if (active && enemyType?.colorHex) setEnemyTypeColor(enemyType.colorHex);
      });
    }
    return () => { active = false; };
  }, [enemyTypeId]);

  const {
    entries: visibleLogEntries,
    handleLogScroll,
    logRef,
    startIndex: eventLogStartIndex,
  } = useEventLog({
    isOpen: windowState.eventLog.open,
    messages,
  });

  const player = useMemo(() => user || {}, [user]);
  const isInCamp = Boolean(baseCamp?.isInCamp);
  const collectingLocked = isCollectingAction(player?.currentAction);
  const inventoryActionsDisabled = areInventoryActionsDisabled(player);
  const inventoryActionsDisabledReason =
    'Pack and Loadout actions are unavailable during combat or while dead.';
  const workbench = baseCamp?.buildings?.find(building => (
    building.buildingKey === 'workbench'
  ));
  const workbenchLevel = Number(
    workbench?.currentLevel
    ?? (workbench?.status === 'completed' ? 1 : 0),
  );
  const canSalvage = isInCamp
    && Number.isFinite(workbenchLevel)
    && workbenchLevel > 0;
  const salvageDisabledReason = !isInCamp
    ? 'Enter your Camp to salvage equipment.'
    : 'Build the Workbench before salvaging equipment.';
  const knownRecipeIds = useMemo(() => Array.from(new Set([
    ...(Array.isArray(baseCamp?.knownRecipeIds) ? baseCamp.knownRecipeIds : []),
    ...(Array.isArray(player?.knownRecipeIds) ? player.knownRecipeIds : []),
  ].map(recipeId => String(recipeId || '').trim()).filter(Boolean))), [
    baseCamp?.knownRecipeIds,
    player?.knownRecipeIds,
  ]);

  const storeQueuedWorldIntent = useCallback(intent => {
    queuedWorldIntentRef.current = intent;
    setQueuedWorldIntent(intent);
  }, []);

  const clearQueuedWorldIntent = useCallback((message = '') => {
    queuedWorldIntentRef.current = null;
    setQueuedWorldIntent(null);
    if (message) ToastService.push(message, 'warning');
  }, []);

  const consumeQueuedWorldIntent = useCallback(key => {
    if (queuedWorldIntentRef.current?.key !== key) return;
    queuedWorldIntentRef.current = null;
    setQueuedWorldIntent(null);
  }, []);

  const requestWorldIntent = useCallback(async intent => {
    if (!intent?.key) return false;
    const action = String(player?.currentAction || '').toUpperCase();
    const hasCombat = Boolean(player?.userCombat)
      || ['FIGHTING', 'FLEEING'].includes(action);
    if (hasCombat || action === 'DEAD') return false;

    const shouldQueue = ['SCAVENGING', 'COLLECTING'].includes(action)
      || (action === 'MOVING' && intent.kind !== 'tile');
    if (!shouldQueue) {
      clearQueuedWorldIntent();
      return true;
    }

    const existing = queuedWorldIntentRef.current;
    const repeated = existing?.key === intent.key;
    const queued = {
      ...intent,
      regionId: region?.id ?? region?.Id ?? '',
    };
    storeQueuedWorldIntent(queued);
    try {
      await GameAPI.SetDesiredAction('RESTING', {
        forceIfRepeated: repeated,
      });
      ToastService.push(
        repeated
          ? `${queued.label} will start now.`
          : `${queued.label} queued as the next action.`,
        'info',
      );
    } catch (error) {
      if (queuedWorldIntentRef.current === queued) {
        clearQueuedWorldIntent();
      }
      throw error;
    }
    return false;
  }, [
    clearQueuedWorldIntent,
    player?.currentAction,
    player?.userCombat,
    region?.Id,
    region?.id,
    storeQueuedWorldIntent,
  ]);

  useEffect(() => {
    if (inventoryActionsDisabled && draggedItem) {
      setDraggedItem(null);
      setItemInteractionAnnouncement('Item selection cancelled.');
    }
  }, [draggedItem, inventoryActionsDisabled]);

  useEffect(() => {
    if (!draggedItem?.item) return;
    const current = draggedItem.fromComponent === 'inventory'
      ? player?.inventory?.some(entry => (
        Number(entry.x) === Number(draggedItem.rowIndex)
        && Number(entry.y) === Number(draggedItem.colIndex)
        && entry.itemId === draggedItem.item.itemId
        && (entry.itemInstanceId || '') === (draggedItem.item.itemInstanceId || '')
      ))
      : draggedItem.fromComponent === 'equipment'
      ? player?.equipment?.some(entry => (
        entry.itemInstanceId === draggedItem.item.itemInstanceId
      ))
      : draggedItem.fromComponent === 'quickBelt'
      ? player?.quickBelt?.some(entry => (
        Number(entry.slotIndex) === Number(draggedItem.slotIndex)
        && entry.itemId === draggedItem.item.itemId
      ))
      : true;
    if (!current) {
      setDraggedItem(null);
      setItemInteractionAnnouncement('Item selection cancelled because its source changed.');
    }
  }, [draggedItem, player?.equipment, player?.inventory, player?.quickBelt]);

  useEffect(() => {
    consumeOperationIdsRef.current.clear();
    learnRecipeOperationIdsRef.current.clear();
    salvageOperationIdsRef.current.clear();
    beltOperationIdsRef.current.clear();
    championEncounterOperationIdsRef.current.clear();
    combatRewardAutoOpenIdsRef.current.clear();
    combatRewardRefreshKeysRef.current.clear();
    pendingCombatManifestRef.current = null;
  }, [player?.currentRunId]);

  useEffect(() => {
    if (connectionStatus !== 'ready') return undefined;
    let active = true;
    Promise.all([getAvatarConfig(), getPlayerAvatar()])
      .then(([config, selection]) => {
        if (!active) return;
        setAvatarConfig(config);
        setAvatarSelection(selection);
      })
      .catch(error => console.error('Failed to load avatar configuration', error));
    return () => { active = false; };
  }, [connectionStatus]);

  useEffect(() => {
    if (player?.avatarSelection && !avatarSelection) setAvatarSelection(player.avatarSelection);
  }, [avatarSelection, player?.avatarSelection]);
  const healthStatMax = Number(player?.statsCached?.health?.valueTo) || Number(player?.statsCached?.health?.valueFrom) || 0;
  let userMaxHp = healthStatMax;
  if (!Number.isFinite(userMaxHp) || userMaxHp <= 0) {
    userMaxHp = Number(player?.attributes?.Vitality) || 0;
  }
  if (!Number.isFinite(userMaxHp) || userMaxHp <= 0) {
    userMaxHp = Math.max(Number(player?.currentHp) || 0, healthStatMax, 1);
  }
  const currentHpPercentage = userMaxHp > 0 ? ((Number(player?.currentHp) || 0) / userMaxHp) * 100 : 0;

  const {
    currentLevelXp,
    level,
    xpForNextLevel,
    xpProgress,
  } = resolvePlayerXpProgress(player?.xp);

  const idleSimulationPausedAt = Number(player?.idleSimulationPausedAt || 0);
  const idlePaused = Number.isFinite(idleSimulationPausedAt) && idleSimulationPausedAt > 0;
  const idlePauseAllowedMsRaw = player?.idlePauseAllowedMs;
  const idlePauseAllowedMs = idlePauseAllowedMsRaw !== undefined ? Number(idlePauseAllowedMsRaw) : Number.NaN;
  const idlePauseAllowedMinutes = Number.isFinite(idlePauseAllowedMs) && idlePauseAllowedMs > 0
    ? idlePauseAllowedMs / 60000
    : null;
  const idleSimulatedUntilRaw = player?.idleSimulatedUntil;
  const idleSimulatedUntil = idleSimulatedUntilRaw !== undefined ? Number(idleSimulatedUntilRaw) : null;
  const lastActivityTsRaw = player?.lastActivity;
  const lastActivityTs = lastActivityTsRaw !== undefined ? Number(lastActivityTsRaw) : null;

  useEffect(() => {
    const media = window.matchMedia('(prefers-reduced-motion: reduce)');
    const update = () => setReduceMotion(media.matches);
    update();
    media.addEventListener('change', update);
    return () => media.removeEventListener('change', update);
  }, []);

  const handleKeyboard = useCallback((event) => {
    if (event.defaultPrevented) return;
    if (event.metaKey || event.ctrlKey || event.altKey) return;
    const active = document.activeElement;
    if (active && (active.tagName === 'INPUT' || active.tagName === 'TEXTAREA' || active.contentEditable === 'true')) {
      return;
    }
    const key = event.key.toLowerCase();
    if (key === 'escape') {
      if (draggedItem) {
        event.preventDefault();
        setDraggedItem(null);
        setItemInteractionAnnouncement('Item selection cancelled.');
        return;
      }
      if (isMenuOpen) {
        event.preventDefault();
        setIsMenuOpen(false);
        menuRef.current?.querySelector('.game-menu__toggle')?.focus();
        return;
      }
      if (isInCamp) return;
      const openPanels = PANEL_ORDER.filter(k => (
        windowState[k]?.open && !windowState[k]?.pinned
      ));
      if (openPanels.length > 0) {
        const lastKey = [...windowLayers].reverse().find(k => openPanels.includes(k));
        if (lastKey) {
          event.preventDefault();
          closeWindow(lastKey);
        }
      }
      return;
    }

    if (isInCamp) return;

    const target = Object.entries(PANEL_CONFIG).find(([, cfg]) => cfg.shortcut.toLowerCase() === key);
    if (target) {
      event.preventDefault();
      togglePanel(target[0], document.activeElement);
    }
  }, [closeWindow, draggedItem, isInCamp, isMenuOpen, togglePanel, windowLayers, windowState]);

  const toggleMenu = useCallback(() => {
    setIsMenuOpen(prev => !prev);
  }, []);

  const openAppearance = useCallback(async () => {
    try {
      const [config, selection] = await Promise.all([getAvatarConfig(), getPlayerAvatar()]);
      setAvatarConfig(config);
      setAvatarSelection(selection);
      setAppearanceOpen(true);
    } catch (error) {
      console.error('Failed to refresh avatar configuration', error);
      ToastService.push('Unable to load the latest appearance options. Try again.', 'error');
    }
  }, []);

  const handleMenuAction = useCallback((action) => {
    setIsMenuOpen(false);
    if (action === 'appearance') {
      openAppearance();
      return;
    }
    if (action === 'logout') {
      try {
        WebSocketService.stop();
      } catch (err) {
        console.warn('Failed to stop websocket before logout', err);
      }
      window.localStorage.removeItem('token');
      window.location.href = '/login';
      return;
    }

    let message = '';
    if (action === 'profile') message = 'Profile management coming soon.';
    if (action === 'password') message = 'Password updates are not available yet.';
    if (action === 'email') message = 'Email changes are not available yet.';
    if (action === 'hall') message = 'Hall of Fame is not available yet.';
    if (message) {
      ToastService.push(message, 'info');
    }
  }, [openAppearance]);

  const saveAppearance = useCallback(async (selection) => {
    const saved = await savePlayerAvatar(selection);
    setAvatarSelection(saved);
    setAppearanceOpen(false);
    ToastService.push('Appearance saved.', 'success');
  }, []);

  useEffect(() => {
    if (!isMenuOpen) return undefined;
    const handlePointerDown = (event) => {
      if (!menuRef.current) return;
      if (!menuRef.current.contains(event.target)) {
        setIsMenuOpen(false);
      }
    };
    document.addEventListener('pointerdown', handlePointerDown);
    return () => document.removeEventListener('pointerdown', handlePointerDown);
  }, [isMenuOpen]);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyboard);
    return () => window.removeEventListener('keydown', handleKeyboard);
  }, [handleKeyboard]);

  useEffect(() => {
    const idlePaused = Number(user?.idleSimulationPausedAt || 0) > 0;
    if (!idlePaused) {
      setResumingIdle(false);
      setIdleResumeError(null);
    }
  }, [user?.idleSimulationPausedAt]);

  useEffect(() => {
    if (!hasUserCombat || player?.currentAction === 'FLEEING') {
      setCombatTimers({ player: null, enemy: null });
      setCombatEffects([]);
      return;
    }
    const now = Date.now();
    const nextTimers = {
      player: buildCombatTimer({
        attackInterval: playerAttackInterval,
        nextAttackTimer: playerNextAttackTimer,
      }, now),
      enemy: buildCombatTimer({
        attackInterval: enemyAttackInterval,
        nextAttackTimer: enemyNextAttackTimer,
      }, now),
    };
    setCombatTimers(prev => ({
      player: prev.player ?? nextTimers.player,
      enemy: prev.enemy ?? nextTimers.enemy,
    }));
  }, [
    combatEnemyId,
    enemyAttackInterval,
    enemyNextAttackTimer,
    hasUserCombat,
    player?.currentAction,
    playerAttackInterval,
    playerNextAttackTimer,
  ]);

  useEffect(() => {
    if (player?.currentAction !== 'FLEEING') {
      setFleeProgress({ attempt: 1, status: '', timer: null });
      return;
    }

    setFleeProgress(current => (
      current.timer
        ? current
        : {
          attempt: Math.max(1, Number(fleeState?.tickCount || 0) + 1),
          status: current.status || 'Finding an opening…',
          timer: buildFleeTimerFromState(fleeState),
        }
    ));
  }, [
    combatEnemyId,
    fleeState,
    player?.currentAction,
  ]);

  useEffect(() => {
    if (combatEffects.length === 0) return undefined;
    const nextExpiry = Math.min(...combatEffects.map(effect => effect.expiresAt));
    const timeoutId = window.setTimeout(() => {
      const now = Date.now();
      setCombatEffects(prev => prev.filter(effect => effect.expiresAt > now));
    }, Math.max(0, nextExpiry - Date.now()));
    return () => window.clearTimeout(timeoutId);
  }, [combatEffects]);

  const onDragStart = useCallback((item) => {
    if (inventoryActionsDisabled) return;
    setDraggedItem(item);
    const quantity = Number(item?.quantity);
    setItemInteractionAnnouncement(
      `${quantity > 0 ? `${quantity} × ` : ''}${item?.item?.name || item?.item?.itemId || 'Item'} selected. Choose a destination.`,
    );
  }, [inventoryActionsDisabled]);

  const onDrop = useCallback(() => {
    setDraggedItem(null);
  }, []);

  const transferQuickBelt = useCallback(async transfer => {
    if (inventoryActionsDisabled) {
      setDraggedItem(null);
      ToastService.push(inventoryActionsDisabledReason, 'warning');
      return null;
    }
    const operationKey = JSON.stringify([
      player?.currentRunId || 'run',
      transfer,
    ]);
    let operationId = beltOperationIdsRef.current.get(operationKey);
    if (!operationId) {
      operationId = createGroundLootOperationId();
      beltOperationIdsRef.current.set(operationKey, operationId);
    }
    try {
      const result = await GameAPI.transferQuickBelt({
        ...transfer,
        operationId,
      });
      reconcileUserSnapshot(result);
      beltOperationIdsRef.current.delete(operationKey);
      setDraggedItem(null);
      setItemInteractionAnnouncement('Quick belt updated.');
      return result;
    } catch (error) {
      if (shouldDiscardLearnRecipeOperationId(error)) {
        beltOperationIdsRef.current.delete(operationKey);
        setDraggedItem(null);
        setItemInteractionAnnouncement('Belt transfer cancelled.');
      }
      throw error;
    }
  }, [
    inventoryActionsDisabled,
    inventoryActionsDisabledReason,
    player?.currentRunId,
    reconcileUserSnapshot,
  ]);

  const consumeItem = useCallback(async (item, itemDetails) => {
    if (inventoryActionsDisabled) {
      ToastService.push(inventoryActionsDisabledReason, 'warning');
      return null;
    }
    const currentHpBefore = Number(player?.currentHp);
    const operationKey = [
      player?.currentRunId || 'run',
      item?.source || 'pack',
      item?.itemId,
      item?.source === 'belt' ? item?.slotIndex : item?.x,
      item?.source === 'belt' ? '' : item?.y,
    ].join(':');
    let operationId = consumeOperationIdsRef.current.get(operationKey);
    if (!operationId) {
      operationId = createGroundLootOperationId();
      consumeOperationIdsRef.current.set(operationKey, operationId);
    }
    const result = await GameAPI.consumeItem({
      itemId: item.itemId,
      source: item.source || 'pack',
      ...(item.source === 'belt'
        ? { slotIndex: Number(item.slotIndex) }
        : { x: Number(item.x), y: Number(item.y) }),
      operationId,
    });
    const reconciled = reconcileUserSnapshot(result);
    if (!reconciled) {
      await refreshUser();
    }
    consumeOperationIdsRef.current.delete(operationKey);
    const currentHp = Number(result?.currentHp ?? result?.CurrentHp);
    const reportedHealed = Number(result?.healed ?? result?.Healed);
    const healed = Number.isFinite(reportedHealed)
      ? reportedHealed
      : (
        Number.isFinite(currentHp)
        && Number.isFinite(currentHpBefore)
          ? Math.max(0, currentHp - currentHpBefore)
          : Number.NaN
      );
    const satietyRestored = Number(
      result?.satietyRestored ?? result?.SatietyRestored,
    );
    const hasTimedEffects = getConsumableEffects(itemDetails)
      .some(isTimedConsumableEffect);
    const effects = describeConsumableUseEffects(
      healed,
      satietyRestored,
      hasTimedEffects,
    );
    const currentHpText = healed > 0 && Number.isFinite(currentHp)
      ? ` — now at ${currentHp} HP.`
      : '.';
    ToastService.push(
      `Used ${itemDetails?.name || 'consumable'}. ${
        effects.join('. ') || 'No effect applied'
      }${currentHpText}`,
      effects.length > 0 ? 'success' : 'info',
    );
    return result;
  }, [
    inventoryActionsDisabled,
    inventoryActionsDisabledReason,
    player?.currentRunId,
    player?.currentHp,
    reconcileUserSnapshot,
    refreshUser,
  ]);

  const learnRecipe = useCallback(async (item, itemDetails) => {
    if (inventoryActionsDisabled) {
      ToastService.push(inventoryActionsDisabledReason, 'warning');
      return null;
    }
    const outputItemId = itemDetails?.teachesRecipeForItemId
      ?? itemDetails?.TeachesRecipeForItemId;
    if (!outputItemId) {
      throw new Error('This Pack item does not teach a recipe.');
    }
    const operationKey = [
      player?.currentRunId || 'run',
      item?.itemId,
      item?.x,
      item?.y,
    ].join(':');
    let operationId = learnRecipeOperationIdsRef.current.get(operationKey);
    if (!operationId) {
      operationId = createGroundLootOperationId();
      learnRecipeOperationIdsRef.current.set(operationKey, operationId);
    }
    let result;
    try {
      result = await GameAPI.learnRecipe({
        operationId,
        itemId: item.itemId,
        x: Number(item.x),
        y: Number(item.y),
      });
    } catch (error) {
      if (shouldDiscardLearnRecipeOperationId(error)) {
        learnRecipeOperationIdsRef.current.delete(operationKey);
      }
      throw error;
    }
    const reconciled = reconcileUserSnapshot(result);
    if (!reconciled) {
      await refreshUser();
    }
    const learnedRecipeIds = result?.knownRecipeIds ?? result?.KnownRecipeIds;
    if (baseCamp && Array.isArray(learnedRecipeIds)) {
      reconcileBaseCampSnapshot({
        ...baseCamp,
        knownRecipeIds: learnedRecipeIds,
      });
    } else if (Array.isArray(learnedRecipeIds)) {
      try {
        await refreshBaseCamp();
      } catch (error) {
        console.error(
          'Recipe learned, but Camp recipe status could not be refreshed.',
          error,
        );
      }
    }
    learnRecipeOperationIdsRef.current.delete(operationKey);
    return result;
  }, [
    baseCamp,
    inventoryActionsDisabled,
    inventoryActionsDisabledReason,
    player?.currentRunId,
    reconcileBaseCampSnapshot,
    reconcileUserSnapshot,
    refreshBaseCamp,
    refreshUser,
  ]);

  const salvageItem = useCallback(async item => {
    if (inventoryActionsDisabled || !canSalvage) {
      ToastService.push(
        inventoryActionsDisabled
          ? inventoryActionsDisabledReason
          : salvageDisabledReason,
        'warning',
      );
      return null;
    }
    if (!item?.itemInstanceId) {
      throw new Error('This equipment instance cannot be salvaged.');
    }
    const operationKey = [
      player?.currentRunId || 'run',
      item.source || 'pack',
      item.itemInstanceId,
      item.source === 'vault' ? item.sourceSlotIndex : item.x,
      item.source === 'vault' ? '' : item.y,
    ].join(':');
    let operationId = salvageOperationIdsRef.current.get(operationKey);
    if (!operationId) {
      operationId = createGroundLootOperationId();
      salvageOperationIdsRef.current.set(operationKey, operationId);
    }
    try {
      const result = await GameAPI.salvageItem({
        operationId,
        itemId: item.itemId,
        itemInstanceId: item.itemInstanceId,
        confirmStarterRemoval: item.confirmStarterRemoval === true,
        source: item.source || 'pack',
        ...(item.source === 'vault'
          ? { sourceSlotIndex: Number(item.sourceSlotIndex) }
          : { x: Number(item.x), y: Number(item.y) }),
      });
      const resultInventory = result?.inventory ?? result?.Inventory;
      if (Array.isArray(resultInventory)) {
        reconcileInventory(
          resultInventory,
          result?.operationId ?? result?.OperationId ?? operationId,
          result,
        );
      } else {
        await refreshUser();
      }
      salvageOperationIdsRef.current.delete(operationKey);
      await Promise.allSettled([refreshUser(), refreshBaseCamp()]);
      ToastService.push('Equipment salvaged at the Workbench.', 'success');
      return result;
    } catch (error) {
      if (shouldDiscardLearnRecipeOperationId(error)) {
        salvageOperationIdsRef.current.delete(operationKey);
      }
      await Promise.allSettled([refreshUser()]);
      throw error;
    }
  }, [
    canSalvage,
    inventoryActionsDisabled,
    inventoryActionsDisabledReason,
    player?.currentRunId,
    reconcileInventory,
    refreshBaseCamp,
    refreshUser,
    salvageDisabledReason,
  ]);

  const reconcileMarketMutation = useCallback(result => {
    const resultInventory = result?.inventory ?? result?.Inventory;
    if (Array.isArray(resultInventory)) {
      reconcileInventory(
        resultInventory,
        result?.operationId ?? result?.OperationId,
        result,
      );
      return;
    }
    refreshUser().catch(error => {
      console.error('Market changed, but the Pack could not be refreshed.', error);
    });
  }, [reconcileInventory, refreshUser]);

  const reconcileCraftMutation = useCallback(result => {
    reconcileBaseCampSnapshot(result);
    const inventory = result?.inventory ?? result?.Inventory;
    if (Array.isArray(inventory)) {
      reconcileInventory(inventory, null, result);
    } else {
      refreshUser().catch(error => {
        console.error('Crafting changed, but the Pack could not be refreshed.', error);
      });
    }
  }, [reconcileBaseCampSnapshot, reconcileInventory, refreshUser]);

  const reconcileTravelResult = useCallback(result => (
    reconcileRegionTransitionResult(result, {
      reconcileUserSnapshot,
      refreshBaseCamp,
      refreshUser,
    })
  ), [reconcileUserSnapshot, refreshBaseCamp, refreshUser]);

  const {
    campActionError,
    campBusyBuildingKey,
    campStorageBusy,
    campTravelBusy,
    campRespawnBusy,
    cancelCampBuilding,
    depositCampMaterials,
    enterCamp,
    installCampRoute,
    refreshCampSafely,
    setCampRespawnPoint,
    startCampBuilding,
    transferCampStorage,
    travelCampRoute,
  } = useBaseCampActions({
    reconcileBaseCampSnapshot,
    reconcileInventory,
    reconcileRegionTransition: reconcileTravelResult,
    refreshBaseCamp,
    refreshUser,
  });
  const {
    cancelGathering,
    gatheringCancelBusy,
  } = useGatheringActions({
    refreshResourceNodes,
    refreshUser,
  });

  const startChampionEncounter = useCallback(async champion => {
    if (!champion?.placementId) {
      throw new Error('This champion is no longer available.');
    }
    const operationKey = `champion:${champion.placementId}`;
    let operationId = championEncounterOperationIdsRef.current.get(
      operationKey,
    );
    if (!operationId) {
      operationId = createGroundLootOperationId();
      championEncounterOperationIdsRef.current.set(
        operationKey,
        operationId,
      );
    }

    try {
      const result = await GameAPI.startChampionEncounter(
        champion.placementId,
        { operationId },
      );
      // The start DTO has no event cursor and may arrive after the combat has
      // already ended over WebSocket. Re-read the authoritative user instead
      // of reviving that stale FIGHTING snapshot.
      await refreshUser();
      championEncounterOperationIdsRef.current.delete(operationKey);
      ToastService.push(
        `Challenging ${champion.name || 'the region champion'}…`,
        'success',
      );
      return result;
    } catch (error) {
      await Promise.allSettled([
        refreshGroundLoot(),
        refreshResourceNodes(),
        refreshUser(),
      ]);
      throw error;
    }
  }, [
    refreshGroundLoot,
    refreshResourceNodes,
    refreshUser,
  ]);

  const groundLootAllowed = !isInCamp
    && canUseGroundLoot(player?.currentAction);
  const playerPosition = useMemo(() => {
    const x = Number(player?.lastLocationX);
    const y = Number(player?.lastLocationY);
    return Number.isInteger(x) && Number.isInteger(y)
      ? { x, y }
      : { x: 0, y: 0 };
  }, [player?.lastLocationX, player?.lastLocationY]);
  useEffect(() => {
    if (!isInCamp) return;
    setDraggedItem(null);
    setGroundLootDropRequest(null);
    setGroundLootWindow(null);
  }, [isInCamp]);
  const requestCampEnter = useCallback(entrance => {
    setWorldTravelRequest({
      entrance,
      kind: entrance?.installed ? 'camp-enter' : 'camp-install',
    });
  }, []);
  const requestCampRoute = useCallback(route => {
    if (!route?.installed) return;
    setWorldTravelRequest({ kind: 'camp-route', route });
  }, []);
  const requestRegionExit = useCallback(exit => {
    if (!exit || exit.enabled === false) return;
    setWorldTravelRequest({ kind: 'region-exit', exit });
  }, []);
  const closeWorldTravel = useCallback(() => {
    setWorldTravelRequest(null);
  }, []);
  const travelRegion = useCallback(async (exit, operationId) => {
    const result = await GameAPI.travelRegionExit(
      exit.key,
      { operationId },
    );
    await reconcileRegionTransitionResult(result, {
      reconcileUserSnapshot,
      refreshBaseCamp,
      refreshUser,
    });
    ToastService.push(
      `Travelled through ${exit.label}.`,
      'success',
    );
    return result;
  }, [
    reconcileUserSnapshot,
    refreshBaseCamp,
    refreshUser,
  ]);

  const executeConfirmedWorldTravel = useCallback(async intent => {
    const target = ['camp-enter', 'camp-install'].includes(intent.kind)
      ? intent.entrance
      : intent.exit;
    if (!target) return null;
    const activate = intent.kind === 'camp-enter'
      ? () => enterCamp({ operationId: intent.operationId })
      : intent.kind === 'camp-install'
        ? () => installCampRoute(
          intent.entrance,
          intent.operationId,
        )
        : () => travelRegion(intent.exit, intent.operationId);
    const response = await GameAPI.SetMovementTarget(
      target.x,
      target.y,
      { stopWithinDistance: 1, arrivalAction: 'RESTING' },
    );
    const result = await response.json();
    if (Number(result.pathLength) === 0) return activate();
    setPendingCampEntry({
      ...target,
      activate,
      label: intent.label,
      routeObserved: false,
    });
    return result;
  }, [enterCamp, installCampRoute, travelRegion]);

  const confirmWorldTravel = useCallback(async operationId => {
    const request = worldTravelRequest;
    if (!request) return null;
    if (request.kind === 'camp-route') {
      return travelCampRoute(request.route, operationId);
    }

    const intent = ['camp-enter', 'camp-install'].includes(request.kind)
      ? {
        ...request,
        id: request.kind === 'camp-install'
          ? `camp-install:${request.entrance.regionId}`
          : 'camp-entrance',
        key: request.kind === 'camp-install'
          ? `camp-install:${request.entrance.regionId}`
          : 'camp-entrance',
        label: request.kind === 'camp-install'
          ? `${request.entrance.regionName} cableway`
          : 'Camp',
        operationId,
        owner: 'home',
      }
      : {
        ...request,
        id: `exit:${request.exit.key}`,
        key: `exit:${request.exit.key}`,
        label: request.exit.label,
        operationId,
        owner: 'home',
      };
    const executeNow = await requestWorldIntent(intent);
    return executeNow ? executeConfirmedWorldTravel(intent) : intent;
  }, [
    executeConfirmedWorldTravel,
    requestWorldIntent,
    travelCampRoute,
    worldTravelRequest,
  ]);
  useEffect(() => {
    if (!pendingCampEntry) return;
    const movementTargetX = Number(player?.movementTargetX);
    const movementTargetY = Number(player?.movementTargetY);
    const action = String(player?.currentAction || '').toUpperCase();
    const hasMovementTarget = player?.movementTargetX !== null
      && player?.movementTargetX !== undefined
      && player?.movementTargetY !== null
      && player?.movementTargetY !== undefined
      && Number.isInteger(movementTargetX)
      && Number.isInteger(movementTargetY);
    const targetMatches = movementTargetX === pendingCampEntry.x
      && movementTargetY === pendingCampEntry.y;
    if (targetMatches) {
      if (!pendingCampEntry.routeObserved) {
        setPendingCampEntry(current => current && ({
          ...current,
          routeObserved: true,
        }));
      }
      return;
    }
    if (hasMovementTarget) {
      setPendingCampEntry(null);
      return;
    }
    if (
      !pendingCampEntry.routeObserved
      || action === 'MOVING'
    ) return;

    setPendingCampEntry(null);
    if (
      isNearGroundLoot(playerPosition, pendingCampEntry)
      && ['IDLE', 'RESTING'].includes(action)
    ) {
      Promise.resolve(pendingCampEntry.activate?.()).catch(error => {
        ToastService.push(
          error?.message
            || `${pendingCampEntry.label || 'Travel'} could not be completed.`,
          'error',
        );
      });
    }
  }, [
    pendingCampEntry,
    player?.currentAction,
    player?.movementTargetX,
    player?.movementTargetY,
    playerPosition,
  ]);

  useEffect(() => {
    if (!queuedWorldIntent) return;
    const currentRegionId = region?.id ?? region?.Id ?? '';
    const action = String(player?.currentAction || '').toUpperCase();
    if (action === 'DEAD') {
      clearQueuedWorldIntent();
      return;
    }
    if (
      queuedWorldIntent.regionId
      && currentRegionId
      && queuedWorldIntent.regionId !== currentRegionId
    ) {
      clearQueuedWorldIntent(
        `${queuedWorldIntent.label} was cleared because the region changed.`,
      );
    }
  }, [
    clearQueuedWorldIntent,
    player?.currentAction,
    queuedWorldIntent,
    region?.Id,
    region?.id,
  ]);

  useEffect(() => {
    if (queuedWorldIntent?.owner !== 'home') return;
    const action = String(player?.currentAction || '').toUpperCase();
    const hasMovementTarget = player?.movementTargetX !== null
      && player?.movementTargetX !== undefined
      && player?.movementTargetY !== null
      && player?.movementTargetY !== undefined;
    if (!['IDLE', 'RESTING'].includes(action) || hasMovementTarget) return;

    consumeQueuedWorldIntent(queuedWorldIntent.key);
    executeConfirmedWorldTravel(queuedWorldIntent).catch(error => {
      ToastService.push(
        error?.message
          || `${queuedWorldIntent.label} is no longer available.`,
        'warning',
      );
    });
  }, [
    consumeQueuedWorldIntent,
    executeConfirmedWorldTravel,
    player?.currentAction,
    player?.movementTargetX,
    player?.movementTargetY,
    queuedWorldIntent,
  ]);

  const requestGroundLootDrop = useCallback((
    item,
    itemDetails,
    target = playerPosition,
    selectedQuantity = 0,
  ) => {
    if (isInCamp || !canUseGroundLoot(player?.currentAction)) {
      ToastService.push('You cannot drop items during your current action.', 'warning');
      return;
    }
    const quantity = Math.min(
      Number(item.quantity) || 1,
      Number(selectedQuantity) || Number(item.quantity) || 1,
    );
    setGroundLootDropRequest({
      item: { ...item, quantity },
      itemDetails: itemDetails || GameAPI.fetchItemAutoCached(item.itemId, item.itemInstanceId),
      target: { x: Number(target.x), y: Number(target.y) },
    });
  }, [isInCamp, player?.currentAction, playerPosition]);

  const requestMapGroundLootDrop = useCallback((dragRecord, target) => {
    setDraggedItem(null);
    if (!dragRecord?.item) return;
    requestGroundLootDrop(
      dragRecord.item,
      GameAPI.fetchItemAutoCached(
        dragRecord.item.itemId,
        dragRecord.item.itemInstanceId,
      ),
      target,
      dragRecord.quantity,
    );
  }, [requestGroundLootDrop]);

  const confirmGroundLootDrop = useCallback(async (quantity, operationId) => {
    const request = groundLootDropRequest;
    if (!request) return;
    try {
      const result = await GameAPI.dropGroundLoot({
        operationId,
        sourceX: Number(request.item.x),
        sourceY: Number(request.item.y),
        itemId: request.item.itemId,
        itemInstanceId: request.item.itemInstanceId,
        quantity,
        targetX: request.target.x,
        targetY: request.target.y,
      });
      const resultInventory = result?.inventory ?? result?.Inventory;
      if (Array.isArray(resultInventory)) {
        reconcileInventory(
          resultInventory,
          result.operationId ?? result.OperationId ?? operationId,
          result,
        );
      } else {
        await refreshUser();
      }
      setGroundLootDropRequest(null);
      ToastService.push('Item dropped. It will disappear in five minutes.', 'success');
      try {
        await refreshGroundLoot();
      } catch (error) {
        console.error('Failed to refresh ground treasure after dropping an item', error);
      }
    } catch (error) {
      const refreshResults = await Promise.allSettled([
        refreshUser(),
        refreshGroundLoot(),
      ]);
      if (
        refreshResults[0]?.status === 'fulfilled'
        && isDropSourceUnchanged(
          refreshResults[0].value?.inventory
            ?? refreshResults[0].value?.Inventory,
          request,
        )
        && isTerminalGroundLootDropError(error)
        && error
        && typeof error === 'object'
      ) {
        error.groundLootUseNewOperationId = true;
      }
      refreshResults
        .filter(result => result.status === 'rejected')
        .forEach(result => console.error(
          'Failed to reconcile ground treasure after a drop conflict',
          result.reason,
        ));
      throw error;
    }
  }, [
    groundLootDropRequest,
    reconcileInventory,
    refreshGroundLoot,
    refreshUser,
  ]);

  const loadGroundLootContainer = useCallback(async (containerId, summary, announceLoading = true) => {
    if (!containerId) return null;
    groundLootLoadSequenceRef.current += 1;
    const loadSequence = groundLootLoadSequenceRef.current;
    openGroundLootContainerRef.current = containerId;
    openGroundLootModeRef.current = 'shared';
    setGroundLootWindow(previous => ({
      mode: 'shared',
      containerId,
      summary: summary || previous?.summary || null,
      detail: previous?.mode === 'shared'
        && previous?.containerId === containerId
        ? previous.detail
        : null,
      loading: announceLoading,
      error: '',
    }));
    try {
      const payload = await GameAPI.fetchGroundLootContainer(containerId);
      const detail = normalizeGroundLootDetail(payload);
      if (
        openGroundLootContainerRef.current !== containerId
        || groundLootLoadSequenceRef.current !== loadSequence
      ) return null;
      setGroundLootWindow({
        mode: 'shared',
        containerId,
        summary: detail.container || summary || null,
        detail,
        loading: false,
        error: '',
      });
      return detail;
    } catch (error) {
      if (
        openGroundLootContainerRef.current !== containerId
        || groundLootLoadSequenceRef.current !== loadSequence
      ) return null;
      setGroundLootWindow(previous => ({
        ...previous,
        loading: false,
        error: groundLootErrorMessage(
          error,
          'The treasure pack could not be opened. Move closer and try again.',
        ),
      }));
      return null;
    }
  }, []);

  const loadWorldLootContainer = useCallback(async (
    containerId,
    summary,
    announceLoading = true,
  ) => {
    if (!containerId) return null;
    groundLootLoadSequenceRef.current += 1;
    const loadSequence = groundLootLoadSequenceRef.current;
    openGroundLootContainerRef.current = containerId;
    openGroundLootModeRef.current = 'world';
    setGroundLootWindow(previous => ({
      mode: 'world',
      containerId,
      summary: summary || previous?.summary || null,
      detail: previous?.mode === 'world'
        && previous?.containerId === containerId
        ? previous.detail
        : null,
      loading: announceLoading,
      error: '',
    }));
    try {
      let payload;
      const shouldOpen = summary?.status === 'empty'
        && !summary?.refillAtUtc;
      if (shouldOpen) {
        try {
          payload = await GameAPI.openWorldLootContainer(containerId, {
            operationId: createGroundLootOperationId(),
          });
        } catch (openError) {
          const code = openError?.details?.code
            ?? openError?.details?.Code;
          if (!['operation_in_progress'].includes(code)) throw openError;
          payload = await GameAPI.fetchWorldLootContainer(containerId);
        }
      } else {
        payload = await GameAPI.fetchWorldLootContainer(containerId);
      }
      const detail = normalizeWorldLootDetail(payload);
      if (
        openGroundLootContainerRef.current !== containerId
        || groundLootLoadSequenceRef.current !== loadSequence
      ) return null;
      setGroundLootWindow({
        mode: 'world',
        containerId,
        summary: detail.container || summary || null,
        detail,
        loading: false,
        error: '',
      });
      return detail;
    } catch (error) {
      if (
        openGroundLootContainerRef.current !== containerId
        || groundLootLoadSequenceRef.current !== loadSequence
      ) return null;
      setGroundLootWindow(previous => ({
        ...previous,
        loading: false,
        error: groundLootErrorMessage(
          error,
          'The loot container could not be opened. Move closer and try again.',
        ),
      }));
      return null;
    }
  }, []);

  const loadStarterCache = useCallback(async (summary, announceLoading = true) => {
    const cacheId = summary?.cacheId ?? summary?.containerId;
    if (!cacheId) return null;
    groundLootLoadSequenceRef.current += 1;
    const loadSequence = groundLootLoadSequenceRef.current;
    openGroundLootContainerRef.current = cacheId;
    openGroundLootModeRef.current = 'starter';
    setGroundLootWindow(previous => ({
      mode: 'starter',
      containerId: cacheId,
      summary,
      detail: previous?.mode === 'starter'
        && previous?.containerId === cacheId
        ? previous.detail
        : null,
      loading: announceLoading,
      error: '',
    }));
    try {
      const payload = await GameAPI.fetchStarterCache();
      const detail = normalizeStarterCacheDetail(payload);
      if (
        openGroundLootContainerRef.current !== cacheId
        || groundLootLoadSequenceRef.current !== loadSequence
      ) return null;
      if (!detail.container || detail.entries.length === 0) {
        setGroundLootWindow(null);
        await refreshStarterCache();
        return detail;
      }
      setGroundLootWindow({
        mode: 'starter',
        containerId: cacheId,
        summary: detail.container || summary,
        detail,
        loading: false,
        error: '',
      });
      return detail;
    } catch (error) {
      if (
        openGroundLootContainerRef.current !== cacheId
        || groundLootLoadSequenceRef.current !== loadSequence
      ) return null;
      setGroundLootWindow(previous => ({
        ...previous,
        loading: false,
        error: groundLootErrorMessage(
          error,
          'Your private Starter Cache could not be opened. Try again.',
        ),
      }));
      return null;
    }
  }, [refreshStarterCache]);

  const openGroundLoot = useCallback((summary) => {
    if (!canUseGroundLoot(player?.currentAction)) {
      ToastService.push('You cannot loot treasure during your current action.', 'warning');
      return;
    }
    groundLootWorldSignatureRef.current = groundLootSummarySignature(
      summary.containerId,
      summary,
    );
    loadGroundLootContainer(summary.containerId, summary);
  }, [loadGroundLootContainer, player?.currentAction]);

  const openCombatGroundLoot = useCallback(() => {
    const summary = combatRewardDraftRef.current?.groundContainer;
    if (summary) openGroundLoot(summary);
  }, [openGroundLoot]);

  useEffect(() => {
    const settledReward = combatReward?.manifestFinal
      ? combatReward
      : pendingCombatManifestRef.current;
    if (!settledReward?.complete || !settledReward?.manifestFinal) return;
    const rewardId = String(settledReward.rewardId || '');
    const groundContainer = settledReward.groundContainer;
    const hasGroundRemainder = Boolean(
      (settledReward.groundLoot || []).length && groundContainer,
    );
    const syncedGroundContainer = resolveCombatGroundContainer(
      settledReward,
      regionWorldState?.groundLoot,
    );
    const championName = String(regionWorldState?.champion?.name || '');
    const isFixedChampionReward = Boolean(
      championName
      && championName === String(settledReward.enemyName || ''),
    );

    if (rewardId && hasGroundRemainder && !syncedGroundContainer) {
      const refreshKey = `ground:${rewardId}`;
      if (!combatRewardRefreshKeysRef.current.has(refreshKey)) {
        combatRewardRefreshKeysRef.current.add(refreshKey);
        refreshGroundLoot().catch(error => {
          combatRewardRefreshKeysRef.current.delete(refreshKey);
          console.error('Failed to refresh combat ground loot', error);
        });
      }
    }
    if (rewardId && isFixedChampionReward) {
      const refreshKey = `resources:${rewardId}`;
      if (!combatRewardRefreshKeysRef.current.has(refreshKey)) {
        combatRewardRefreshKeysRef.current.add(refreshKey);
        refreshResourceNodes().catch(error => {
          combatRewardRefreshKeysRef.current.delete(refreshKey);
          console.error('Failed to refresh champion progress', error);
        });
      }
    }

    if (!hasGroundRemainder) {
      if (pendingCombatManifestRef.current?.rewardId === rewardId) {
        pendingCombatManifestRef.current = null;
      }
      return;
    }
    if (
      !rewardId
      || !syncedGroundContainer
      || combatRewardAutoOpenIdsRef.current.has(rewardId)
      || !canAutoOpenCombatGroundLoot(
        settledReward,
        player?.currentAction,
        playerPosition,
      )
    ) {
      return;
    }
    combatRewardAutoOpenIdsRef.current.add(rewardId);
    if (pendingCombatManifestRef.current?.rewardId === rewardId) {
      pendingCombatManifestRef.current = null;
    }
    openGroundLoot(syncedGroundContainer);
  }, [
    combatReward,
    openGroundLoot,
    player?.currentAction,
    playerPosition,
    refreshGroundLoot,
    refreshResourceNodes,
    regionWorldState?.champion,
    regionWorldState?.groundLoot,
  ]);

  const openWorldLoot = useCallback((summary) => {
    if (!canUseGroundLoot(player?.currentAction)) {
      ToastService.push(
        'You cannot loot containers during your current action.',
        'warning',
      );
      return;
    }
    groundLootWorldSignatureRef.current = groundLootSummarySignature(
      summary.containerId,
      summary,
    );
    loadWorldLootContainer(summary.containerId, summary);
  }, [loadWorldLootContainer, player?.currentAction]);

  const openStarterCache = useCallback((summary = starterCache) => {
    if (!summary) return;
    loadStarterCache(summary);
  }, [loadStarterCache, starterCache]);

  const closeGroundLoot = useCallback(() => {
    groundLootLoadSequenceRef.current += 1;
    openGroundLootContainerRef.current = null;
    openGroundLootModeRef.current = null;
    groundLootWorldSignatureRef.current = null;
    setGroundLootWindow(null);
    setGroundLootTransferBusy(false);
    takeOperationIdsRef.current.clear();
  }, []);

  const refreshOpenGroundLoot = useCallback(async () => {
    const containerId = openGroundLootContainerRef.current;
    if (!containerId) return null;
    const detail = openGroundLootModeRef.current === 'world'
      ? await loadWorldLootContainer(containerId, null, false)
      : await loadGroundLootContainer(containerId, null, false);
    try {
      await refreshGroundLoot();
    } catch (error) {
      console.error('Failed to refresh region treasure markers', error);
    }
    return detail;
  }, [
    loadGroundLootContainer,
    loadWorldLootContainer,
    refreshGroundLoot,
  ]);

  const refreshOpenStarterCache = useCallback(async () => {
    const cacheId = openGroundLootContainerRef.current;
    if (!cacheId) return null;
    const summary = await refreshStarterCache();
    if (!summary || summary.cacheId !== cacheId) {
      closeGroundLoot();
      return null;
    }
    return loadStarterCache(summary, false);
  }, [closeGroundLoot, loadStarterCache, refreshStarterCache]);

  const dropPackItemIntoOpenGroundLoot = useCallback(async (
    item,
    quantity,
    operationId,
    destinationSlot,
  ) => {
    const containerId = openGroundLootContainerRef.current;
    const mode = openGroundLootModeRef.current;
    const target = groundLootWindow?.detail?.container
      || groundLootWindow?.summary;
    if (
      !containerId
      || !['shared', 'world'].includes(mode)
      || !Number.isInteger(Number(target?.x))
      || !Number.isInteger(Number(target?.y))
    ) {
      throw new Error('This treasure pack is no longer available.');
    }
    if (groundLootTransferBusy) {
      throw new Error('Another treasure transfer is already in progress.');
    }

    const request = {
      item: { ...item },
      target: { x: Number(target.x), y: Number(target.y) },
    };
    setGroundLootTransferBusy(true);
    setGroundLootWindow(previous => previous
      ? { ...previous, error: '' }
      : previous);
    try {
      const result = mode === 'world'
        ? await GameAPI.depositWorldLoot(containerId, {
          operationId,
          sourceX: Number(item.x),
          sourceY: Number(item.y),
          itemId: item.itemId,
          itemInstanceId: item.itemInstanceId,
          quantity,
          slot: destinationSlot,
        })
        : await GameAPI.dropGroundLoot({
          operationId,
          sourceX: Number(item.x),
          sourceY: Number(item.y),
          itemId: item.itemId,
          itemInstanceId: item.itemInstanceId,
          quantity,
          targetX: request.target.x,
          targetY: request.target.y,
        });
      const resultInventory = result?.inventory ?? result?.Inventory;
      if (Array.isArray(resultInventory)) {
        reconcileInventory(
          resultInventory,
          result.operationId ?? result.OperationId ?? operationId,
          result,
        );
      } else {
        await refreshUser();
      }
      ToastService.push(
        mode === 'world'
          ? 'Item deposited into the shared chest.'
          : 'Item moved into the treasure pack.',
        'success',
      );
      await refreshOpenGroundLoot();
      return result;
    } catch (error) {
      const refreshResults = await Promise.allSettled([
        refreshUser(),
        refreshOpenGroundLoot(),
      ]);
      if (
        refreshResults[0]?.status === 'fulfilled'
        && isDropSourceUnchanged(
          refreshResults[0].value?.inventory
            ?? refreshResults[0].value?.Inventory,
          request,
        )
        && isTerminalGroundLootDropError(error)
        && error
        && typeof error === 'object'
      ) {
        error.groundLootUseNewOperationId = true;
      }
      refreshResults
        .filter(result => result.status === 'rejected')
        .forEach(result => console.error(
          'Failed to reconcile treasure after a Pack transfer',
          result.reason,
        ));
      throw error;
    } finally {
      setGroundLootTransferBusy(false);
    }
  }, [
    groundLootTransferBusy,
    groundLootWindow?.detail?.container,
    groundLootWindow?.summary,
    reconcileInventory,
    refreshOpenGroundLoot,
    refreshUser,
  ]);

  const takeGroundLoot = useCallback(async (entry, destination) => {
    const containerId = openGroundLootContainerRef.current;
    if (!containerId || groundLootTransferBusy) return;
    const destinationKey = destination ? `${destination.x}:${destination.y}` : 'auto';
    const operationKey = `${containerId}:${entry.entryId}:${destinationKey}`;
    let operationId = takeOperationIdsRef.current.get(operationKey);
    if (!operationId) {
      operationId = createGroundLootOperationId();
      takeOperationIdsRef.current.set(operationKey, operationId);
    }

    setGroundLootTransferBusy(true);
    setGroundLootWindow(previous => previous ? { ...previous, error: '' } : previous);
    try {
      const authored = openGroundLootModeRef.current === 'world';
      const result = await (
        authored ? GameAPI.takeWorldLoot : GameAPI.takeGroundLoot
      )(containerId, entry.entryId, {
        operationId,
        destinationX: destination?.x,
        destinationY: destination?.y,
      });
      const resultInventory = result?.inventory ?? result?.Inventory;
      if (Array.isArray(resultInventory)) {
        reconcileInventory(
          resultInventory,
          result.operationId ?? result.OperationId ?? operationId,
          result,
        );
      } else {
        await refreshUser();
      }
      takeOperationIdsRef.current.delete(operationKey);
      ToastService.push(
        authored
          ? 'Loot moved to your Pack.'
          : 'Treasure moved to your Pack.',
        'success',
      );
      const hasContainerResult = result
        && (
          Object.prototype.hasOwnProperty.call(result, 'container')
          || Object.prototype.hasOwnProperty.call(result, 'Container')
        );
      const resultContainer = result?.container ?? result?.Container;
      if (hasContainerResult && !resultContainer) {
        closeGroundLoot();
        try {
          await refreshGroundLoot();
        } catch (refreshError) {
          console.error(
            'Failed to refresh region treasure markers after emptying a pack',
            refreshError,
          );
        }
        return;
      }
      await refreshOpenGroundLoot();
    } catch (error) {
      setGroundLootWindow(previous => previous ? {
        ...previous,
        error: groundLootErrorMessage(
          error,
          'The item could not be transferred. The treasure and your Pack were refreshed.',
        ),
      } : previous);
      try {
        const [, refreshedDetail] = await Promise.all([
          refreshUser(),
          refreshOpenGroundLoot(),
        ]);
        if (
          isTerminalGroundLootTakeError(error)
          && containsGroundLootEntry(refreshedDetail, entry.entryId)
        ) {
          takeOperationIdsRef.current.delete(operationKey);
        }
      } catch (refreshError) {
        console.error('Failed to reconcile ground treasure after a transfer conflict', refreshError);
      }
    } finally {
      setGroundLootTransferBusy(false);
    }
  }, [
    groundLootTransferBusy,
    closeGroundLoot,
    reconcileInventory,
    refreshGroundLoot,
    refreshOpenGroundLoot,
    refreshUser,
  ]);

  const takeStarterCache = useCallback(async (entry) => {
    const cacheId = openGroundLootContainerRef.current;
    if (!cacheId || groundLootTransferBusy) return;
    const operationKey = `starter:${cacheId}:${entry.entryId}`;
    let operationId = takeOperationIdsRef.current.get(operationKey);
    if (!operationId) {
      operationId = createGroundLootOperationId();
      takeOperationIdsRef.current.set(operationKey, operationId);
    }
    setGroundLootTransferBusy(true);
    setGroundLootWindow(previous => previous
      ? { ...previous, error: '' }
      : previous);
    try {
      const result = await GameAPI.takeStarterCache(
        cacheId,
        entry.entryId,
        { operationId },
      );
      const reconciled = reconcileUserSnapshot(result);
      if (!reconciled) {
        await refreshUser();
      }
      takeOperationIdsRef.current.delete(operationKey);
      const nextSummary = await refreshStarterCache();
      ToastService.push('Starter gear moved to your Pack.', 'success');
      if (!nextSummary) {
        closeGroundLoot();
        openPack();
        return;
      }
      await loadStarterCache(nextSummary, false);
    } catch (error) {
      setGroundLootWindow(previous => previous ? {
        ...previous,
        error: groundLootErrorMessage(
          error,
          'The starter item could not be transferred. Your Pack and cache were refreshed.',
        ),
      } : previous);
      try {
        await Promise.all([
          refreshUser(),
          refreshOpenStarterCache(),
        ]);
      } catch (refreshError) {
        console.error(
          'Failed to reconcile the Starter Cache after a transfer error',
          refreshError,
        );
      }
    } finally {
      setGroundLootTransferBusy(false);
    }
  }, [
    closeGroundLoot,
    groundLootTransferBusy,
    loadStarterCache,
    openPack,
    reconcileUserSnapshot,
    refreshOpenStarterCache,
    refreshStarterCache,
    refreshUser,
  ]);

  useEffect(() => {
    const containerId = groundLootWindow?.containerId;
    if (
      !containerId
      || !['shared', 'world'].includes(groundLootWindow?.mode)
      || groundLootTransferBusy
    ) return;
    const summaries = groundLootWindow.mode === 'world'
      ? regionWorldState?.worldLootContainers
      : regionWorldState?.groundLoot;
    const summary = (summaries || []).find(
      candidate => candidate.containerId === containerId,
    );
    const worldSignature = groundLootSummarySignature(containerId, summary);
    if (groundLootWorldSignatureRef.current === worldSignature) {
      if (!summary && !groundLootWindow.loading) {
        closeGroundLoot();
        ToastService.push(
          groundLootWindow.mode === 'world'
            ? 'That loot container is no longer available.'
            : 'That treasure pack is now empty.',
          'info',
        );
      }
      return;
    }
    groundLootWorldSignatureRef.current = worldSignature;
    if (!summary) {
      if (!groundLootWindow.loading) {
        closeGroundLoot();
        ToastService.push(
          groundLootWindow.mode === 'world'
            ? 'That loot container is no longer available.'
            : 'That treasure pack is now empty.',
          'info',
        );
      }
      return;
    }
    if (groundLootWindow.mode === 'world') {
      loadWorldLootContainer(containerId, summary, false);
    } else {
      loadGroundLootContainer(containerId, summary, false);
    }
  }, [
    closeGroundLoot,
    groundLootTransferBusy,
    groundLootWindow?.containerId,
    groundLootWindow?.loading,
    groundLootWindow?.mode,
    loadGroundLootContainer,
    loadWorldLootContainer,
    regionWorldState,
  ]);

  useEffect(() => {
    const containerId = groundLootWindow?.containerId;
    if (
      !containerId
      || !['shared', 'world'].includes(groundLootWindow?.mode)
      || groundLootTransferBusy
    ) return;
    const summary = groundLootWindow?.summary
      || (groundLootWindow.mode === 'world'
        ? regionWorldState?.worldLootContainers || []
        : regionWorldState?.groundLoot || []).find(
        candidate => candidate.containerId === containerId,
      );
    if (
      canUseGroundLoot(player?.currentAction)
      && isNearGroundLoot(playerPosition, summary)
    ) {
      return;
    }
    closeGroundLoot();
    ToastService.push(
      canUseGroundLoot(player?.currentAction)
        ? 'Move back next to the treasure pack to open it.'
        : 'The treasure pack closed because your action changed.',
      'info',
    );
  }, [
    closeGroundLoot,
    groundLootTransferBusy,
    groundLootWindow?.containerId,
    groundLootWindow?.mode,
    groundLootWindow?.summary,
    player?.currentAction,
    playerPosition,
    regionWorldState,
  ]);

  useEffect(() => {
    if (!idlePaused) return undefined;
    setIdleOverlayTick(Date.now());
    const interval = window.setInterval(() => {
      setIdleOverlayTick(Date.now());
    }, 10000);
    return () => window.clearInterval(interval);
  }, [idlePaused]);

  const shellClass = useMemo(() => {
    const classes = ['game-shell', 'theme-progresslands'];
    if (reduceMotion) classes.push('reduce-motion');
    if (collectingLocked) classes.push('is-collecting');
    if (
      hasUserCombat
      || ['FIGHTING', 'FLEEING'].includes(
        String(player?.currentAction || '').toUpperCase(),
      )
    ) classes.push('is-in-combat');
    return classes.join(' ');
  }, [collectingLocked, hasUserCombat, player?.currentAction, reduceMotion]);

  const getCombatStats = useCallback((sourceStats) => {
    if (!sourceStats) return [];

    const parseNumber = (value) => {
      const num = Number(value);
      if (!Number.isFinite(num)) return null;
      return num;
    };

    const formatNumber = (num) => Number(num.toFixed(2)).toString();

    const formatValue = (stat) => {
      if (stat == null) return null;
      if (typeof stat === 'number' || typeof stat === 'string') {
        const numeric = parseNumber(stat);
        if (numeric !== null) {
          return { display: formatNumber(numeric), isZero: numeric === 0 };
        }
        const str = String(stat).trim();
        return str ? { display: str, isZero: false } : null;
      }

      if (typeof stat === 'object') {
        const fromNum = parseNumber(stat.valueFrom ?? stat.min ?? stat.from ?? stat.low);
        const toNum = parseNumber(stat.valueTo ?? stat.max ?? stat.to ?? stat.high);
        if (fromNum !== null || toNum !== null) {
          if (fromNum !== null && toNum !== null) {
            const fromDisp = formatNumber(fromNum);
            const toDisp = formatNumber(toNum);
            const display = fromDisp === toDisp ? fromDisp : `${fromDisp} - ${toDisp}`;
            return { display, isZero: fromNum === 0 && toNum === 0 };
          }
          const singleNum = fromNum ?? toNum;
          return { display: formatNumber(singleNum), isZero: singleNum === 0 };
        }

        const singleNum = parseNumber(stat.value ?? stat.amount ?? stat.base ?? stat.current ?? stat.total);
        if (singleNum !== null) {
          return { display: formatNumber(singleNum), isZero: singleNum === 0 };
        }

        if (stat.value !== undefined) {
          const str = String(stat.value).trim();
          if (str) {
            return { display: str, isZero: str === '0' }; // string zero best effort
          }
        }
      }

      return null;
    };

    const formatLabel = (key, stat) => {
      if (key && key.length === 24 && stat && typeof stat === 'object') {
        const name = stat.name || stat.statName;
        if (typeof name === 'string' && name.trim()) {
          return name.trim().toUpperCase();
        }
      }

      return key
        .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
        .replace(/[_\s]+/g, ' ')
        .trim()
        .toUpperCase();
    };

    const formatCombatValue = (label, display) => {
      const normalizedLabel = label.toLowerCase().replace(/[^a-z0-9]/g, '');
      if (['critchance', 'critdamage', 'dodge'].includes(normalizedLabel)) {
        const values = String(display).split(' - ').map(value => Number(value));
        if (values.every(Number.isFinite)) {
          return values.map(value => `${formatNumber(value * 100)}%`).join(' - ');
        }
      }
      if (normalizedLabel === 'weaponattackspeed') {
        return `${display} APM`;
      }
      return display;
    };

    return Object.entries(sourceStats)
      .reduce((acc, [key, stat]) => {
        const value = formatValue(stat);
        if (!value || value.display === '') return acc;
        const label = formatLabel(key, stat);
        if (!isCombatDisplayStat(key, label)) return acc;
        acc.push({
          key,
          label,
          value: formatCombatValue(label, value.display),
          isZero: Boolean(value.isZero),
        });
        return acc;
      }, [])
      .sort((a, b) => a.label.localeCompare(b.label));
  }, []);

  const setDesiredAction = useCallback(async (action) => {
    clearQueuedWorldIntent();
    try {
      await GameAPI.SetDesiredAction(action);
    } catch (err) {
      console.error('Failed to set desired action', err);
    }
  }, [clearQueuedWorldIntent]);

  const requestCampRest = useCallback(async () => {
    const result = await GameAPI.SetDesiredAction('RESTING');
    ToastService.push('Resting requested at your Sleeping Place.', 'success');
    return result;
  }, []);

  const resumeIdleProgression = useCallback(async (actionOverride) => {
    if (resumingIdle) return;
    setResumingIdle(true);
    setIdleResumeError(null);
    try {
      const actionToResume = actionOverride || 'IDLE';
      await GameAPI.SetDesiredAction(actionToResume);
    } catch (err) {
      console.error('Failed to resume idle progression', err);
      setIdleResumeError('Failed to resume progression. Please try again.');
    } finally {
      setResumingIdle(false);
    }
  }, [resumingIdle]);

  const combatOverlay = useMemo(() => {
    const enemy = resolveCombatEnemy(player);
    if (enemy) {
      const enemyHealthMax = Number(enemy?.statsCached?.health?.valueTo)
        || Number(enemy?.statsCached?.health?.valueFrom)
        || Number(enemy?.baseHP)
        || Number(enemy?.currentHp)
        || 1;
      const enemyHpPercent = enemyHealthMax > 0 ? ((Number(enemy?.currentHp) || 0) / enemyHealthMax) * 100 : 0;
      const playerCombatStats = getCombatStats(player?.statsCached);
      const enemyCombatStats = getCombatStats(enemy?.statsCached);
      const recentLog = messages.slice(-8);
      const fleeAction = player?.currentAction === 'FLEEING' ? 'FIGHTING' : 'FLEEING';
      const fleeLabel = player?.currentAction === 'FLEEING' ? 'Return to Combat' : 'Flee';

      return (
        <CombatOverlay
          player={player}
          enemy={enemy}
          enemyLevel={resolveDisplayedEnemyLevel(
            player?.userCombat,
            region?.enemyLevel,
          )}
          enemyTypeColor={enemyTypeColor}
          currentHpPercentage={Math.min(Math.max(currentHpPercentage, 0), 100)}
          userMaxHp={userMaxHp}
          enemyHpPercent={enemyHpPercent}
          enemyHealthMax={enemyHealthMax}
          playerCombatStats={playerCombatStats}
          enemyCombatStats={enemyCombatStats}
          recentLog={recentLog}
          combatEffects={combatEffects}
          avatarConfig={avatarConfig}
          avatarSelection={avatarSelection}
          playerTimer={combatTimers.player}
          enemyTimer={combatTimers.enemy}
          fleeProgress={fleeProgress}
          fleeAction={fleeAction}
          fleeLabel={fleeLabel}
          onSetDesiredAction={setDesiredAction}
        />
      );
    }
    return null;
  }, [
    currentHpPercentage,
    avatarConfig,
    avatarSelection,
    combatEffects,
    combatTimers.enemy,
    combatTimers.player,
    enemyTypeColor,
    fleeProgress,
    getCombatStats,
    messages,
    player,
    region?.enemyLevel,
    setDesiredAction,
    userMaxHp,
  ]);

  const idleOverlay = useMemo(() => {
    if (!idlePaused) return null;

    const nowMarker = idleOverlayTick || Date.now();
    const pausedAtDate = Number.isFinite(idleSimulationPausedAt) ? new Date(idleSimulationPausedAt) : null;
    const simulatedUntilDate = Number.isFinite(idleSimulatedUntil) && idleSimulatedUntil > 0 ? new Date(idleSimulatedUntil) : null;
    const lastActivityDate = Number.isFinite(lastActivityTs) && lastActivityTs > 0 ? new Date(lastActivityTs) : null;
    const allowedLabel = idlePauseAllowedMinutes
      ? (idlePauseAllowedMinutes >= 10
        ? Math.round(idlePauseAllowedMinutes)
        : Math.round(idlePauseAllowedMinutes * 10) / 10)
      : null;
    const pausedSinceMs = pausedAtDate ? Math.max(0, nowMarker - pausedAtDate.getTime()) : null;
    const pausedLabel = pausedSinceMs !== null
      ? (pausedSinceMs < 60000 ? '< 1 min' : `${Math.floor(pausedSinceMs / 60000)} min`)
      : null;
    const lastActivityDiff = lastActivityDate ? Math.max(0, nowMarker - lastActivityDate.getTime()) : null;
    const lastActivityLabel = lastActivityDiff !== null
      ? (lastActivityDiff < 60000 ? '< 1 min ago' : `${Math.floor(lastActivityDiff / 60000)} min ago`)
      : null;

    const normalizeAction = (action) => {
      if (!action || typeof action !== 'string') return '';
      if (action === 'NONE') return '';
      return action;
    };

    const resumeActionCandidate = normalizeAction(player?.idleResumeAction)
      || normalizeAction(player?.desiredAction)
      || normalizeAction(player?.currentAction);
    const resumeAction = resumeActionCandidate || 'IDLE';

    const formatActionLabel = (action) => {
      if (!action) return 'Progression';
      const lower = action.toLowerCase().replace(/_/g, ' ');
      return lower.charAt(0).toUpperCase() + lower.slice(1);
    };

    const resumeLabel = formatActionLabel(resumeAction);
    const continueLabel = resumingIdle ? 'Continuing…' : `Continue ${resumeLabel}`;

    const metaItems = [];
    if (pausedLabel) metaItems.push(`Paused for ${pausedLabel}`);
    if (lastActivityLabel) metaItems.push(`Last activity: ${lastActivityLabel}`);
    if (simulatedUntilDate) metaItems.push(`Progress saved through ${simulatedUntilDate.toLocaleTimeString()}`);
    if (resumeAction) metaItems.push(`Resume action: ${resumeLabel}`);

    return (
      <IdleOverlay
        allowedLabel={allowedLabel}
        metaItems={metaItems}
        idleResumeError={idleResumeError}
        continueLabel={continueLabel}
        onContinue={() => resumeIdleProgression(resumeAction)}
        resumingIdle={resumingIdle}
        resumeAction={resumeAction}
      />
    );
  }, [
    idlePaused,
    idleOverlayTick,
    idleSimulationPausedAt,
    idleSimulatedUntil,
    lastActivityTs,
    idlePauseAllowedMinutes,
    player?.idleResumeAction,
    player?.desiredAction,
    player?.currentAction,
    resumingIdle,
    idleResumeError,
    resumeIdleProgression,
  ]);

  const panelContent = useMemo(() => ({
    playerInfo: (
      <PlayerPanel
        player={player}
        currentHpPercentage={currentHpPercentage}
        userMaxHp={userMaxHp}
        xpProgress={xpProgress}
        xpForNextLevel={xpForNextLevel}
        level={level}
        avatarConfig={avatarConfig}
        avatarSelection={avatarSelection}
        onOpenAppearance={openAppearance}
      />
    ),
    regionInfo: (
      <RegionPanel
        region={region}
        currentAction={player?.currentAction}
      />
    ),
    equipment: (
      <EquipmentPanel
        player={player}
        draggedItem={draggedItem}
        inventoryActionsDisabled={inventoryActionsDisabled}
        inventoryActionsDisabledReason={inventoryActionsDisabledReason}
        onDragStart={onDragStart}
        onDrop={onDrop}
      />
    ),
    inventory: (
      <InventoryPanel
        player={player}
        draggedItem={draggedItem}
        canLearnRecipes={isInCamp}
        canUseGroundLoot={groundLootAllowed}
        inventoryActionsDisabled={inventoryActionsDisabled}
        inventoryActionsDisabledReason={inventoryActionsDisabledReason}
        onDragEnd={onDrop}
        onDragStart={onDragStart}
        onDrop={onDrop}
        onLearnRecipe={learnRecipe}
        onBeltTransfer={transferQuickBelt}
        onRequestDrop={requestGroundLootDrop}
        onUseItem={consumeItem}
        knownRecipeIds={knownRecipeIds}
      />
    ),
    eventLog: (
      <EventLog
        entries={visibleLogEntries}
        onScroll={handleLogScroll}
        ref={logRef}
        startIndex={eventLogStartIndex}
      />
    ),
  }), [
    player,
    currentHpPercentage,
    userMaxHp,
    xpProgress,
    xpForNextLevel,
    level,
    region,
    visibleLogEntries,
    handleLogScroll,
    logRef,
    eventLogStartIndex,
    draggedItem,
    groundLootAllowed,
    isInCamp,
    inventoryActionsDisabled,
    inventoryActionsDisabledReason,
    onDragStart,
    onDrop,
    transferQuickBelt,
    requestGroundLootDrop,
    consumeItem,
    learnRecipe,
    knownRecipeIds,
    avatarConfig,
    avatarSelection,
    openAppearance,
  ]);

  const zIndexMap = useMemo(() => {
    const map = {};
    windowLayers.forEach((key, index) => {
      map[key] = 200 + index;
    });
    return map;
  }, [windowLayers]);

  const compactContent = useMemo(() => ({
    playerInfo: (
      <div className="pl-compact">
        <div className="pl-compact__title">{player?.name || 'Unknown Operative'}</div>
        <ProgressBar label="Operative health" value={currentHpPercentage} variant="hp" />
        <div className="pl-compact__meta">HP {Math.round(Number(player?.currentHp) || 0)} / {Math.round(userMaxHp)} · LVL {level} · {player?.currentAction || 'IDLE'}</div>
      </div>
    ),
    regionInfo: (
      <div className="pl-compact">
        <div className="pl-compact__title">{region?.name || 'Unknown Region'}</div>
        <div className="pl-compact__meta">{player?.currentAction || 'IDLE'} · {messages[messages.length - 1] || 'No fresh disasters reported.'}</div>
      </div>
    ),
    equipment: (
      <div className="pl-compact">
        <div className="pl-compact__title">Loadout</div>
        <div className="pl-compact__meta">{player?.equipment?.length || 0} equipped · probably still not enough</div>
      </div>
    ),
    inventory: (
      <div className="pl-compact">
        <div className="pl-compact__title">Pack</div>
        <div className="pl-compact__meta">{player?.inventory?.length || 0} / 25 slots occupied</div>
      </div>
    ),
    eventLog: (
      <div className="pl-compact">
        {(messages.slice(-2).length ? messages.slice(-2) : ['Awaiting activity…']).map((message, index) => (
          <div className="pl-compact__meta" key={`compact-log-${index}`}>{message}</div>
        ))}
      </div>
    ),
  }), [
    currentHpPercentage,
    level,
    messages,
    player,
    region,
    userMaxHp,
  ]);

  const focusedWindow = windowLayers[windowLayers.length - 1];
  const windowRenderOrder = resolveWindowRenderOrder(windowState);
  const starterOnboardingRequired = !isInCamp
    && !collectingLocked
    && shouldShowStarterOnboarding(player, starterCache);
  const starterCacheNeedsClaim = starterCacheHasRequiredEntries(starterCache);

  if (connectionStatus !== 'ready') {
    const failed = connectionStatus === 'error';
    return (
      <div className={`${shellClass} pl-ui`}>
        <ToastHost />
        <main className="idle-overlay">
          <section
            aria-labelledby="game-connection-title"
            aria-live={failed ? 'assertive' : 'polite'}
            className="idle-overlay__inner"
            role={failed ? 'alert' : 'status'}
          >
            <header className="idle-overlay__header">
              <h1
                className="idle-overlay__title"
                id="game-connection-title"
              >
                {failed ? 'Connection interrupted' : 'Entering Progress Lands'}
              </h1>
              <span className="idle-overlay__subtitle">
                {failed ? 'The game could not finish loading.' : 'Preparing your run…'}
              </span>
            </header>
            {failed ? (
              <>
                <div className="idle-overlay__content">
                  <p>
                    Check that the game server is running, then try again.
                  </p>
                  <p className="idle-overlay__error">
                    {connectionError?.message || 'Initial data request failed.'}
                  </p>
                </div>
                <Button onClick={retryConnection} variant="primary">
                  Try Again
                </Button>
              </>
            ) : null}
          </section>
        </main>
      </div>
    );
  }

  return (
    <div className={`${shellClass} pl-ui`}>
      <ToastHost />
      <span className="pl-sr-only" aria-live="polite" role="status">
        {itemInteractionAnnouncement}
      </span>
      <div
        className="game-shell__map"
        aria-label={isInCamp ? 'Private Camp' : 'World Map'}
      >
        {isInCamp ? (
          <BaseCampScene
            baseCamp={baseCamp}
            busyBuildingKey={campBusyBuildingKey}
            character={{
              currentHpPercentage,
              currentLevelXp,
              equipment: panelContent.equipment,
              level,
              overview: panelContent.playerInfo,
              pack: panelContent.inventory,
              player,
              userMaxHp,
              xpForNextLevel,
              xpProgress,
            }}
            currentAction={player?.currentAction}
            error={campActionError || baseCampError}
            headerMenu={(
              <GameMenu
                embedded
                isOpen={isMenuOpen}
                menuRef={menuRef}
                onAction={handleMenuAction}
                onToggle={toggleMenu}
              />
            )}
            inventory={player?.inventory || []}
            loading={baseCampLoading}
            onCancel={cancelCampBuilding}
            onCraftMutation={reconcileCraftMutation}
            onDeposit={depositCampMaterials}
            onMarketMutation={reconcileMarketMutation}
            onRefresh={refreshCampSafely}
            onRequestRoute={requestCampRoute}
            onRequestRest={requestCampRest}
            onSalvage={salvageItem}
            onSetRespawnPoint={setCampRespawnPoint}
            onStart={startCampBuilding}
            onTransferStorage={transferCampStorage}
            storageBusy={campStorageBusy}
            respawnBusy={campRespawnBusy}
            travelBusy={campTravelBusy}
          />
        ) : (
          <GameTileExperience
            baseCamp={baseCamp}
            avatarConfig={avatarConfig}
            avatarSelection={avatarSelection}
            draggedItem={groundLootAllowed ? draggedItem : null}
            onActivateCampEntrance={requestCampEnter}
            onActivateRegionExit={requestRegionExit}
            onDropInventoryItem={requestMapGroundLootDrop}
            onOpenGroundLoot={openGroundLoot}
            onOpenWorldLoot={openWorldLoot}
            onOpenStarterCache={openStarterCache}
            onClearWorldIntent={clearQueuedWorldIntent}
            onConsumeWorldIntent={consumeQueuedWorldIntent}
            onRequestWorldIntent={requestWorldIntent}
            onRetryResourceNodes={refreshResourceNodes}
            onStartChampionEncounter={startChampionEncounter}
            region={region}
            regionWorldState={regionWorldState}
            resourceNodesError={resourceNodesError}
            resourceNodesLoading={resourceNodesLoading}
            playerLevel={level}
            scavengePreviewVisible={scavengePreviewVisible}
            queuedWorldIntent={queuedWorldIntent}
            starterCache={starterCache}
            user={player}
          />
        )}
      </div>
        {worldTravelRequest ? (
        <WorldTravelDialog
          key={[
            worldTravelRequest.kind,
            worldTravelRequest.exit?.key || '',
            worldTravelRequest.route?.regionId || '',
            worldTravelRequest.entrance?.regionId || '',
          ].join(':')}
          onClose={closeWorldTravel}
          onConfirm={confirmWorldTravel}
          request={worldTravelRequest}
        />
      ) : null}
      {groundLootDropRequest && !isInCamp ? (
        <GroundLootDropDialog
          onClose={() => setGroundLootDropRequest(null)}
          onConfirm={confirmGroundLootDrop}
          request={groundLootDropRequest}
        />
      ) : null}
      {groundLootWindow && !isInCamp ? (
        <GroundLootTransferDialog
          busy={groundLootTransferBusy}
          detail={groundLootWindow.detail || {
            container: groundLootWindow.summary,
            entries: [],
          }}
          error={groundLootWindow.error}
          inventory={player?.inventory || []}
          loading={groundLootWindow.loading}
          onClose={closeGroundLoot}
          onDrop={['shared', 'world'].includes(groundLootWindow.mode)
            ? dropPackItemIntoOpenGroundLoot
            : undefined}
          onRefresh={groundLootWindow.mode === 'starter'
            ? refreshOpenStarterCache
            : refreshOpenGroundLoot}
          onTake={groundLootWindow.mode === 'starter'
            ? takeStarterCache
            : takeGroundLoot}
          containerTitle={groundLootWindow.mode === 'world'
            ? groundLootWindow.detail?.container?.name
              || groundLootWindow.summary?.name
            : undefined}
          nonExpiring={groundLootWindow.mode === 'world'}
          privateMode={groundLootWindow.mode === 'starter'}
          worldContainer={groundLootWindow.mode === 'world'}
          serverTimeOffsetMs={
            regionWorldState?.serverTimeOffsetMs || 0
          }
        />
      ) : null}
      {idleOverlay}
      {!isInCamp ? combatOverlay : null}
      {!isInCamp && combatReward ? (
        <CombatRewardsPopup
          actionsOpen={mobileActionsOpen}
          onClose={closeCombatReward}
          onOpenGroundLoot={openCombatGroundLoot}
          onOpenPack={openPack}
          onPin={pinCombatReward}
          pinned={combatRewardPinned}
          summary={combatReward}
        />
      ) : null}
      {appearanceOpen && avatarConfig && (
        <AvatarCreatorWindow
          config={avatarConfig}
          onClose={() => setAppearanceOpen(false)}
          onSave={saveAppearance}
          playerName={player?.name}
          selection={avatarSelection}
        />
      )}
      {!isInCamp ? (
        <div className="hud-layer">
        <StatusBar
          currentHpPercentage={currentHpPercentage}
          level={level}
          onToggleOperative={event => togglePanel('playerInfo', event.currentTarget)}
          onToggleRegion={event => togglePanel('regionInfo', event.currentTarget)}
          operativeOpen={windowState.playerInfo.open}
          player={player}
          region={region}
          regionOpen={windowState.regionInfo.open}
          userMaxHp={userMaxHp}
          xpForNextLevel={xpForNextLevel}
          xpProgress={xpProgress}
        />
        <div
          className="hud-status-stack"
          data-floating-window-obstacle="true"
        >
          <div className="hud-effect-strip">
            <HungerStatusEffect player={player} />
            <ConsumableEffectsHud
              serverTimeOffsetMs={
                regionWorldState?.serverTimeOffsetMs || 0
              }
              user={player}
            />
          </div>
          <GatheringStatus
            busy={gatheringCancelBusy}
            collection={
              player?.activeResourceCollection
              ?? player?.ActiveResourceCollection
            }
            onCancel={cancelGathering}
          />

          {starterOnboardingRequired ? (
            <aside
              aria-label="Starter loadout"
              className="starter-onboarding"
              role="status"
            >
              <div>
                <strong>Starter loadout required</strong>
                <span>
                  {starterCacheNeedsClaim
                    ? 'Open your Starter Cache, take both items, then equip them from your Pack.'
                    : 'Equip both starter items from your Pack to unlock movement and begin this run.'}
                </span>
              </div>
              <Button
                onClick={starterCacheNeedsClaim
                  ? () => openStarterCache(starterCache)
                  : openPack}
                variant="primary"
              >
                {starterCacheNeedsClaim ? 'Open Starter Cache' : 'Open Pack'}
              </Button>
            </aside>
          ) : null}
        </div>

        <GameMenu
          isOpen={isMenuOpen}
          onToggle={toggleMenu}
          onAction={handleMenuAction}
          menuRef={menuRef}
        />

        <QuickBelt
          capacity={player?.quickBeltCapacity || 1}
          disabled={inventoryActionsDisabled}
          entries={player?.quickBelt || []}
          onUse={(entry, itemDetails) => consumeItem({
            ...entry,
            source: 'belt',
          }, itemDetails)}
        />

        {LAUNCHER_PANEL_ORDER.map((key) => {
          const cfg = PANEL_CONFIG[key];
          const state = windowState[key] || { open: false };
          return (
            <div
              className={`pl-launcher pl-launcher--${cfg.anchor}`}
              data-floating-window-obstacle="true"
              hidden={state.open}
              key={`launcher-${key}`}
            >
              <Button
                aria-controls={`${key}-window`}
                aria-expanded={state.open}
                icon={cfg.icon}
                onClick={event => togglePanel(key, event.currentTarget)}
                variant="ghost"
              >
                <span className="pl-launcher__label">{cfg.title}</span>
              </Button>
            </div>
          );
        })}

        <div className="pl-window-stack">
        {windowRenderOrder.map((key) => {
          const cfg = PANEL_CONFIG[key];
          const state = windowState[key] || {
            compact: false,
            open: false,
            pinned: false,
            position: null,
            size: cfg.defaultSize || null,
          };
          return (
            <FloatingWindow
              anchor={cfg.anchor}
              compact={state.compact}
              compactContent={compactContent[key]}
              focused={focusedWindow === key}
              icon={cfg.icon}
              id={key}
              key={key}
              onClose={closeWindow}
              onCompactToggle={toggleCompact}
              onFocus={focusWindow}
              onMove={moveWindow}
              onPinToggle={togglePin}
              onResize={resizeWindow}
              open={state.open}
              pinned={state.pinned}
              position={state.position}
              resizable={cfg.resizable}
              scaleSizeRange={cfg.scaleSizeRange}
              size={state.size}
              minSize={cfg.minSize}
              maxSize={cfg.maxSize}
              shortcut={cfg.shortcut}
              title={cfg.title}
              zIndex={zIndexMap[key] || 200}
            >
              {panelContent[key]}
            </FloatingWindow>
          );
        })}
        </div>

          <div
            aria-label="Action dock"
            className="pl-action-dock"
            data-floating-window-obstacle="true"
            onFocusCapture={pinCombatReward}
            onMouseEnter={pinCombatReward}
            onPointerDown={pinCombatReward}
          >
            <ActionsPanel
              onRequestAction={setDesiredAction}
              onScavengePreviewChange={setScavengePreviewVisible}
              queuedWorldIntent={queuedWorldIntent}
            />
          </div>

          <div
            className="pl-mobile-actions"
            hidden={!mobileActionsOpen}
            id="mobile-actions-panel"
            onFocusCapture={pinCombatReward}
            onMouseEnter={pinCombatReward}
            onPointerDown={pinCombatReward}
          >
            <ActionsPanel
              enableShortcuts={false}
              onRequestAction={setDesiredAction}
              onScavengePreviewChange={setScavengePreviewVisible}
              queuedWorldIntent={queuedWorldIntent}
            />
          </div>
          <div className="pl-mobile-rail" aria-label="More tools">
            {MOBILE_TOOL_PANEL_ORDER.map(key => (
              <IconButton
                aria-controls={`${key}-window`}
                aria-expanded={windowState[key].open}
                icon={PANEL_CONFIG[key].icon}
                key={`mobile-${key}`}
                label={PANEL_CONFIG[key].title}
                onClick={event => togglePanel(key, event.currentTarget)}
              />
            ))}
          </div>
          <nav
            className="pl-mobile-nav"
            aria-label="Game tools"
            onFocusCapture={pinCombatReward}
            onPointerDown={pinCombatReward}
          >
            <Button
              aria-controls="mobile-actions-panel"
              aria-expanded={mobileActionsOpen}
              icon="move"
              onClick={() => setMobileActionsOpen(open => !open)}
              selected={mobileActionsOpen}
              variant="ghost"
            >Action</Button>
            <Button
              aria-controls="inventory-window"
              aria-expanded={windowState.inventory.open}
              disabled={inventoryActionsDisabled}
              icon="pack"
              onClick={event => togglePanel('inventory', event.currentTarget)}
              selected={windowState.inventory.open}
              variant="ghost"
            >Pack</Button>
          </nav>
        </div>
      ) : null}
    </div>
  );
}

export default GameHome;
